import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { buildPersonnelResolver, resolveTherapistRow, fetchPendingNameKeys } from '../lib/personnelMatcher';
import { useInactiveLabels } from '../hooks/useInactiveLabels';
import autoTable from 'jspdf-autotable';
import { newLandscapePdf, newPortraitPdf, drawCompactHeader } from '../components/visits/pdfUtils';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { MultiSelect } from '../components/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { getVisitWeight as sharedGetVisitWeight } from '../utils/visitCalculations';
import { Search, FileText, Download, X, FileSpreadsheet, ChevronDown, Mail, Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import { computeWeeklyMetrics } from '../utils/visitCalculations';
import { usePermissions } from '../context/PermissionsContext';
import EmailReportsModal from '../components/visits/EmailReportsModal';
import DateRangeReportModal from '../components/visits/DateRangeReportModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Visits({ hideHeader = false }) {
  const withInactive = useInactiveLabels();
  const { hasPermission, isAdmin } = usePermissions();
  const [visits, setVisits] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [taskNames, setTaskNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Tracking config from the Tracking Console
  const [trackingConfig, setTrackingConfig] = useState(null);
  
  // Report preview states
  const [reportPreview, setReportPreview] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [expiringDocsDays, setExpiringDocsDays] = useState(90);
  
  // Missing state tracking
  const [missingStateItems, setMissingStateItems] = useState([]);
  const [showMissingStateModal, setShowMissingStateModal] = useState(false);
  const [pendingReportType, setPendingReportType] = useState(null);

  // Date range report
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [dateRangeType, setDateRangeType] = useState('weekly'); // weekly, monthly, quarterly, annual, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dateRangeReport, setDateRangeReport] = useState('therapists'); // therapists, agencies, lateNotes, missed

  // Email reports modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailReports, setEmailReports] = useState({
    therapists: true, agencies: true, evalDc: true, lateNotes: true, missed: true,
  });
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const [filters, setFilters] = useState({
    profile_ids: [],
    agency_names: [],
    status_types: [],
    task_names: [],
    start_date: '',
    end_date: '',
    date_range_preset: '',
  });

  // Helper: get unit label from tracking config
  const unitLabel = (plural = true) => {
    if (!trackingConfig) return plural ? 'visits' : 'visit';
    return plural ? trackingConfig.unit_label_plural : trackingConfig.unit_label_singular;
  };
  const trackingName = () => trackingConfig?.name || 'Patient Visits';

  // Helper: get visit weight using tracking config
  const getVisitWeightFromConfig = (visit) => {
    return sharedGetVisitWeight(visit.task_name, visit.status, trackingConfig);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchVisits();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      const [profilesRes, agenciesRes, visitsRes, configRes] = await Promise.all([
        axios.get(`${API}/personnel`),
        axios.get(`${API}/agency-profiles`),
        axios.get(`${API}/visits`),
        axios.get(`${API}/tracking-config`).catch(() => ({ data: null })),
      ]);
      if (configRes.data) setTrackingConfig(configRes.data);
      // Sort profiles by last name, then first name
      const sortedProfiles = profilesRes.data.sort((a, b) => {
        const lastNameCompare = (a.last_name || '').localeCompare(b.last_name || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.first_name || '').localeCompare(b.first_name || '');
      });
      setProfiles(sortedProfiles);
      setAgencies(agenciesRes.data);
      
      // Extract unique statuses and task names from visits
      const uniqueStatuses = [...new Set(visitsRes.data.map(v => v.status).filter(Boolean))];
      const uniqueTaskNames = [...new Set(visitsRes.data.map(v => v.task_name).filter(Boolean))].sort();
      setStatuses(uniqueStatuses);
      setTaskNames(uniqueTaskNames);
    } catch (error) {
      toast.error('Failed to load filter options');
    }
  };

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.profile_ids && filters.profile_ids.length > 0) {
        params.profile_id = filters.profile_ids.join(',');
      }
      if (filters.agency_names && filters.agency_names.length > 0) {
        params.agency = filters.agency_names.join(',');
      }
      if (filters.status_types && filters.status_types.length > 0) {
        params.status = filters.status_types.join(',');
      }
      if (filters.task_names && filters.task_names.length > 0) {
        params.task_name = filters.task_names.join(',');
      }
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await axios.get(`${API}/visits`, { params });
      setVisits(response.data);
    } catch (error) {
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.profile_ids && filters.profile_ids.length > 0) {
        params.profile_id = filters.profile_ids.join(',');
      }
      if (filters.agency_names && filters.agency_names.length > 0) {
        params.agency = filters.agency_names.join(',');
      }
      if (filters.status_types && filters.status_types.length > 0) {
        params.status = filters.status_types.join(',');
      }
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await axios.get(`${API}/export`, {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'visits_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel export successful!');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const doc = newLandscapePdf();
      
      // Add title
      doc.setFontSize(18);
      doc.text(`${trackingConfig?.configured ? trackingConfig.name : 'Visits'} Report`, 14, 15);
      
      // Add date range if applied
      doc.setFontSize(10);
      let yPos = 25;
      if (filters.start_date && filters.end_date) {
        doc.text(`Date Range: ${filters.start_date} to ${filters.end_date}`, 14, yPos);
        yPos += 7;
      }
      
      // Add filter summary
      if (filters.profile_ids && filters.profile_ids.length > 0) {
        const selectedProfiles = profiles.filter(p => filters.profile_ids.includes(p.id));
        doc.text(`Profiles: ${selectedProfiles.map(p => `${p.last_name}, ${p.first_name}`).join(', ')}`, 14, yPos);
        yPos += 7;
      }
      if (filters.agency_names && filters.agency_names.length > 0) {
        doc.text(`Agencies: ${filters.agency_names.join(', ')}`, 14, yPos);
        yPos += 7;
      }
      if (filters.status_types && filters.status_types.length > 0) {
        doc.text(`Status: ${filters.status_types.join(', ')}`, 14, yPos);
        yPos += 7;
      }
      
      doc.text(`Total Visits: ${visits.length}`, 14, yPos);
      yPos += 10;
      
      // Prepare table data
      const tableData = visits.map(visit => [
        withInactive(`${visit.first_name} ${visit.last_name}`),
        visit.date || '-',
        visit.task_name || '-',
        visit.task_type || '-',
        visit.status || '-',
        visit.agency || '-',
        visit.city && visit.state ? `${visit.city}, ${visit.state}` : '-'
      ]);
      
      // Add table
      autoTable(doc, {
        head: [['Name', 'Date', 'Task Name', 'Task Type', 'Status', 'Agency', 'Location']],
        body: tableData,
        startY: yPos,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [26, 54, 93] },
        margin: { top: yPos },
      });
      
      // Save PDF
      doc.save(`visits_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF export successful!');
    } catch (error) {
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportWeeklySummaryPDF = async () => {
    // Use the already-calculated reportData from the preview
    if (!reportData || reportPreview !== 'weeklySummary') {
      toast.error('Please preview the report first');
      return;
    }
    
    setExporting(true);
    try {
      const doc = newLandscapePdf();
      
      // Get the last week's Saturday date for the title
      const lastWeekDate = reportData.weeks.length > 0 
        ? reportData.weeks[reportData.weeks.length - 1].saturdayFormatted 
        : '';
      
      drawCompactHeader(doc, { title: 'Therapists Weekly', subtitle: `Week Ending: ${lastWeekDate}` });
      // Add legend
      doc.setFontSize(9);
      let yPos = 24;
      
      // Color legend
      doc.setFillColor(34, 197, 94); // green
      doc.rect(14, yPos - 3, 4, 4, 'F');
      doc.setTextColor(0, 0, 0);
      doc.text('Within 3 of ideal', 20, yPos);
      
      doc.setFillColor(234, 179, 8); // yellow
      doc.rect(70, yPos - 3, 4, 4, 'F');
      doc.text('4-5 from ideal', 76, yPos);
      
      doc.setFillColor(239, 68, 68); // red
      doc.rect(120, yPos - 3, 4, 4, 'F');
      doc.text('6+ from ideal', 126, yPos);
      yPos += 8;
      
      // Helper to get cell color based on ideal
      const getCellColor = (value, ideal) => {
        if (ideal === null || ideal === undefined || value === '-' || value === 0) return null;
        const diff = Math.abs(value - ideal);
        if (diff <= 3) return [220, 252, 231]; // green-100
        if (diff <= 5) return [254, 249, 195]; // yellow-100
        return [254, 226, 226]; // red-100
      };
      
      // Prepare table headers (same as preview)
      const tableHeaders = [
        'Therapist',
        'State',
        'Disc.',
        'Ideal',
        ...reportData.weeks.map(w => w.saturdayFormatted)
      ];
      if (reportData.hasProj1) tableHeaders.push('Proj 1');
      if (reportData.hasProj2) tableHeaders.push('Proj 2');
      
      // Build table data with grand totals at top and state subtotals (matches preview exactly)
      const tableData = [];
      
      // GRAND TOTALS ROW AT TOP
      const grandTotalRow = {
        isGrandTotal: true,
        row: [
          'GRAND TOTAL',
          '',
          '',
          '',
          ...reportData.weekTotals.map(t => ({ value: t, ideal: null }))
        ]
      };
      if (reportData.hasProj1) grandTotalRow.row.push({ value: reportData.proj1Total, ideal: null });
      if (reportData.hasProj2) grandTotalRow.row.push({ value: reportData.proj2Total, ideal: null });
      tableData.push(grandTotalRow);
      
      // Add therapists grouped by state with subtotals
      reportData.stateOrder.forEach(state => {
        const therapistsInState = reportData.therapists.filter(t => (t.state || 'Other') === state);
        
        // Add each therapist in this state
        therapistsInState.forEach(therapist => {
          const therapistRow = {
            isTherapist: true,
            row: [
              withInactive(therapist.name),
              therapist.state || '-',
              therapist.discipline || '-',
              therapist.ideal_visits || '-',
              ...reportData.weeks.map((_, idx) => ({ 
                value: therapist.weeks[idx] || 0, 
                ideal: therapist.ideal_visits 
              }))
            ]
          };
          if (reportData.hasProj1) therapistRow.row.push({ value: therapist.proj1 || 0, ideal: therapist.ideal_visits });
          if (reportData.hasProj2) therapistRow.row.push({ value: therapist.proj2 || 0, ideal: therapist.ideal_visits });
          tableData.push(therapistRow);
        });
        
        // Add state subtotal row
        const subtotal = reportData.stateSubtotals[state];
        const subtotalRow = {
          isStateSubtotal: true,
          state: state,
          row: [
            `${state} Total`,
            '',
            '',
            '',
            ...subtotal.weekTotals.map(t => ({ value: t, ideal: null }))
          ]
        };
        if (reportData.hasProj1) subtotalRow.row.push({ value: subtotal.proj1Total, ideal: null });
        if (reportData.hasProj2) subtotalRow.row.push({ value: subtotal.proj2Total, ideal: null });
        tableData.push(subtotalRow);
      });
      
      // Add table with dark blue header (matches preview's bg-sage: #1a365d)
      autoTable(doc, {
        head: [tableHeaders],
        body: tableData.map(item => item.row.map(cell => 
          typeof cell === 'object' ? cell.value : cell
        )),
        startY: yPos,
        styles: { 
          fontSize: 7,
          cellPadding: 1.5
        },
        headStyles: { 
          fillColor: [26, 54, 93], // Dark blue (#1a365d - matches preview's bg-sage)
          fontStyle: 'bold',
          halign: 'center',
          textColor: [255, 255, 255]
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 12, halign: 'center' },
          2: { cellWidth: 12, halign: 'center' },
          3: { cellWidth: 12, halign: 'center' },
        },
        didParseCell: function(data) {
          const item = tableData[data.row.index];
          if (!item) return;
          
          // Style grand total row - dark blue background with white text (matches preview's bg-sage)
          if (item.isGrandTotal) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [26, 54, 93]; // Dark blue (#1a365d - matches preview)
            data.cell.styles.textColor = [255, 255, 255]; // White text
            data.cell.styles.fontSize = 8;
          }
          
          // Style state subtotal rows - slate gray background (matches preview)
          if (item.isStateSubtotal) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [203, 213, 225]; // slate-300
            data.cell.styles.textColor = [30, 41, 59]; // slate-800
          }
          
          // Apply colors to data cells for therapist rows
          if (item.isTherapist && data.column.index >= 4) {
            const cellData = item.row[data.column.index];
            if (typeof cellData === 'object' && cellData.ideal !== null) {
              const color = getCellColor(cellData.value, cellData.ideal);
              if (color) {
                data.cell.styles.fillColor = color;
              }
            }
          }
          
          // Center align numbers (columns 4 onwards)
          if (data.column.index >= 4) {
            data.cell.styles.halign = 'center';
          }
          
          // Light blue background for projection columns (therapist rows only)
          const projStartCol = 4 + reportData.weeks.length;
          if (item.isTherapist && data.column.index >= projStartCol) {
            const cellData = item.row[data.column.index];
            const color = typeof cellData === 'object' ? getCellColor(cellData.value, cellData.ideal) : null;
            if (!color) {
              data.cell.styles.fillColor = [226, 232, 240]; // slate-200 (light blue-gray)
            }
          }
        }
      });
      
      // Save PDF
      doc.save(`therapists_weekly_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Therapists Weekly PDF exported!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportWeeklyAgencyPDF = async () => {
    if (!reportData || reportPreview !== 'weeklyAgency') {
      toast.error('Please preview the report first');
      return;
    }
    
    setExporting(true);
    try {
      const doc = newLandscapePdf();
      drawCompactHeader(doc, { title: 'Agencies Weekly', subtitle: `Generated: ${new Date().toLocaleDateString()} | Sorted by State → Name` });
      
      // Prepare headers (added State column)
      const headers = [
        'Agency',
        'State',
        ...reportData.weeks.map(w => w.saturdayFormatted)
      ];
      if (reportData.hasProj1) headers.push('Proj 1');
      if (reportData.hasProj2) headers.push('Proj 2');
      
      // Build table data with grand totals at top and state subtotals
      const tableData = [];
      
      // GRAND TOTALS ROW AT TOP
      const grandTotalRow = ['GRAND TOTAL', ''];
      reportData.weekTotals.forEach(t => grandTotalRow.push(t));
      if (reportData.hasProj1) grandTotalRow.push(reportData.proj1Total);
      if (reportData.hasProj2) grandTotalRow.push(reportData.proj2Total);
      tableData.push({ isGrandTotal: true, row: grandTotalRow });
      
      // Add agencies grouped by state with subtotals
      reportData.stateOrder.forEach(state => {
        const agenciesInState = reportData.agencies.filter(a => (a.state || 'Other') === state);
        
        // Add each agency in this state
        agenciesInState.forEach(agency => {
          const agencyRow = [agency.name, agency.state || '-'];
          reportData.weeks.forEach((_, idx) => agencyRow.push(agency.weeks[idx] || 0));
          if (reportData.hasProj1) agencyRow.push(agency.proj1 || 0);
          if (reportData.hasProj2) agencyRow.push(agency.proj2 || 0);
          tableData.push({ isAgency: true, row: agencyRow });
        });
        
        // Add state subtotal row
        const subtotal = reportData.stateSubtotals[state];
        const subtotalRow = [`${state} Total`, ''];
        subtotal.weekTotals.forEach(t => subtotalRow.push(t));
        if (reportData.hasProj1) subtotalRow.push(subtotal.proj1Total);
        if (reportData.hasProj2) subtotalRow.push(subtotal.proj2Total);
        tableData.push({ isStateSubtotal: true, row: subtotalRow });
      });
      
      autoTable(doc, {
        head: [headers],
        body: tableData.map(item => item.row),
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { 
          fillColor: [26, 54, 93], 
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 15, halign: 'center' },
        },
        didParseCell: function(data) {
          const item = tableData[data.row.index];
          if (!item) return;
          
          // Style grand total row - dark sage background with white text (matches preview)
          if (item.isGrandTotal) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [26, 54, 93]; // Dark blue
            data.cell.styles.textColor = [255, 255, 255]; // White text
            data.cell.styles.fontSize = 9;
          }
          
          // Style state subtotal rows - slate gray background (matches preview)
          if (item.isStateSubtotal) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [203, 213, 225]; // slate-300
            data.cell.styles.textColor = [30, 41, 59]; // slate-800
          }
          
          // Highlight projection columns with sage (agency rows only)
          const projColStart = reportData.weeks.length + 2; // +2 for Agency and State columns
          if (item.isAgency && data.column.index >= projColStart) {
            data.cell.styles.fillColor = [230, 237, 247]; // blue/10
          }
          
          // Center align numbers (columns 2 onwards)
          if (data.column.index > 1) {
            data.cell.styles.halign = 'center';
          }
        }
      });
      
      doc.save(`agencies_weekly_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Agencies Weekly PDF exported!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportEvalDischargePDF = async () => {
    if (!reportData || reportPreview !== 'evalDischarge') {
      toast.error('Please preview the report first');
      return;
    }
    
    setExporting(true);
    try {
      const doc = newPortraitPdf();
      drawCompactHeader(doc, { title: 'Agency Evals vs Discharge Weekly' });
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Week Ending: ${reportData.weekEnding ? new Date(reportData.weekEnding + 'T00:00:00').toLocaleDateString() : 'N/A'}`, 14, 22);
      doc.text(`Data Source: ${reportData.projType || 'Projections'}`, 14, 28);
      doc.text(`Report Generated: ${new Date().toLocaleDateString()}`, 14, 34);
      
      // Add totals summary box at top (matches preview)
      doc.setFillColor(240, 242, 241);
      doc.roundedRect(14, 40, 180, 15, 2, 2, 'F');
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL', 30, 49);
      
      doc.setTextColor(26, 54, 93); // Dark blue
      doc.text(`${reportData.totals.evals} Evals`, 70, 49);
      
      doc.setTextColor(200, 107, 86); // clay
      doc.text(`${reportData.totals.discharges} Discharges`, 110, 49);
      
      const diffColor = reportData.totals.difference >= 0 ? [26, 54, 93] : [220, 53, 69];
      doc.setTextColor(...diffColor);
      doc.text(`${reportData.totals.difference >= 0 ? '+' : ''}${reportData.totals.difference} Diff`, 160, 49);
      
      // Build table data (no totals row - it's in summary box)
      const tableData = reportData.items.map(item => [
        item.agency,
        item.evals,
        item.discharges,
        { value: item.difference, isPositive: item.difference >= 0 }
      ]);
      
      autoTable(doc, {
        head: [['Agency', 'Evals', 'Discharges', 'Difference']],
        body: tableData.map(row => [row[0], row[1], row[2], (row[3].isPositive ? '+' : '') + row[3].value]),
        startY: 60,
        styles: { 
          fontSize: 8,
          cellPadding: 3,
          halign: 'center'
        },
        headStyles: { 
          fillColor: [26, 54, 93],
          fontStyle: 'bold',
          textColor: [255, 255, 255]
        },
        columnStyles: {
          0: { cellWidth: 80, halign: 'left' },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 }
        },
        didParseCell: function(data) {
          // Color difference column
          if (data.column.index === 3 && data.section === 'body') {
            const item = tableData[data.row.index];
            if (item) {
              if (item[3].isPositive) {
                data.cell.styles.textColor = [26, 54, 93]; // Dark blue
              } else {
                data.cell.styles.textColor = [220, 53, 69]; // red
              }
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });
      
      doc.save(`agency_evals_vs_discharge_${reportData.weekEnding || new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Eval vs Discharge report exported!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportLateNotesPDF = async () => {
    if (!reportData || reportPreview !== 'lateNotes') {
      toast.error('Please preview the report first');
      return;
    }
    
    setExporting(true);
    try {
      const doc = newLandscapePdf();
      drawCompactHeader(doc, { title: 'Late Notes Weekly Report', color: [200, 107, 86] });
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Report Generated: ${new Date().toLocaleDateString()}`, 14, 22);
      
      // Add totals summary box at top (matches preview)
      doc.setFillColor(240, 242, 241);
      doc.roundedRect(14, 28, 100, 12, 2, 2, 'F');
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'bold');
      doc.text(`Total Late Notes: ${reportData.grandTotal}`, 24, 36);
      
      // Format week headers (MM/DD)
      const weekHeaders = reportData.weekDates.map(w => {
        const d = new Date(w + 'T00:00:00');
        return `${(d.getMonth() + 1)}/${d.getDate()}`;
      });
      
      // Build table data - therapists first, TOTALS row at bottom (matches preview)
      const tableData = reportData.therapists.map(t => ({
        isTherapist: true,
        row: [withInactive(t.therapist_name), ...t.weekly_counts, t.total]
      }));
      
      // Add totals row at bottom
      tableData.push({
        isTotals: true,
        row: ['TOTALS', ...reportData.weekTotals, reportData.grandTotal]
      });
      
      autoTable(doc, {
        head: [['Therapist', ...weekHeaders, 'Total']],
        body: tableData.map(item => item.row),
        startY: 45,
        styles: { 
          fontSize: 8,
          cellPadding: 3,
          halign: 'center'
        },
        headStyles: { 
          fillColor: [26, 54, 93], // Dark blue (matches therapist report)
          fontStyle: 'bold',
          textColor: [255, 255, 255]
        },
        columnStyles: {
          0: { cellWidth: 50, halign: 'left' },
        },
        didParseCell: function(data) {
          if (data.section !== 'body') return;
          
          const item = tableData[data.row.index];
          if (!item) return;
          
          // Color late note counts for therapist rows (not therapist name column)
          if (item.isTherapist && data.column.index > 0) {
            const value = item.row[data.column.index];
            if (value > 0) {
              data.cell.styles.textColor = [200, 107, 86]; // clay color
              data.cell.styles.fontStyle = 'bold';
            }
          }
          
          // Style totals row (last row) - dark sage with white text - MUST come after other styling
          if (item.isTotals) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [26, 54, 93]; // Dark blue
            data.cell.styles.textColor = [255, 255, 255]; // white - force override
          }
        }
      });
      
      doc.save(`late_notes_weekly_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Late Notes report exported!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportMissedVisitsPDF = async () => {
    if (!reportData || reportPreview !== 'missedVisits') {
      toast.error('Please preview the report first');
      return;
    }
    
    setExporting(true);
    try {
      const doc = newLandscapePdf();
      drawCompactHeader(doc, { title: `Missed ${trackingConfig?.configured ? trackingConfig.name : 'Visits'} Weekly Report`, color: [26, 54, 93] });
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Report Generated: ${new Date().toLocaleDateString()}`, 14, 22);
      
      // Add totals summary box at top
      doc.setFillColor(240, 242, 241);
      doc.roundedRect(14, 28, 100, 12, 2, 2, 'F');
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'bold');
      doc.text(`Total Missed ${unitLabel()}: ${reportData.grandTotal}`, 24, 36);
      
      // Format week headers
      const weekHeaders = reportData.weekDates.map(w => {
        const d = new Date(w + 'T00:00:00');
        return `${(d.getMonth() + 1)}/${d.getDate()}`;
      });
      
      // Build table data with state grouping
      const tableData = [];
      
      // Grand total row first
      tableData.push({
        type: 'grand_total',
        row: [`GRAND TOTAL (${reportData.grandTotal})`, ...reportData.weekTotals]
      });
      
      // Each state section
      reportData.stateOrder.forEach(state => {
        const group = reportData.stateGroups[state] || [];
        const stateWeekTotals = reportData.stateWeekTotals[state] || [];
        const stateTotal = reportData.stateTotals[state] || 0;
        
        // State header
        tableData.push({
          type: 'state_header',
          state: state,
          row: [state, ...reportData.weekDates.map(() => '')]
        });
        
        // Therapist rows
        group.forEach(t => {
          tableData.push({
            type: 'therapist',
            row: [withInactive(t.therapist_name), ...t.weekly_counts]
          });
        });
        
        // State subtotal
        tableData.push({
          type: 'state_subtotal',
          state: state,
          row: [`${state} TOTAL (${stateTotal})`, ...stateWeekTotals]
        });
      });
      
      autoTable(doc, {
        head: [['Therapist', ...weekHeaders]],
        body: tableData.map(item => item.row),
        startY: 45,
        styles: { 
          fontSize: 8,
          cellPadding: 3,
          halign: 'center'
        },
        headStyles: { 
          fillColor: [26, 54, 93],
          fontStyle: 'bold',
          textColor: [255, 255, 255]
        },
        columnStyles: {
          0: { cellWidth: 50, halign: 'left' },
        },
        didParseCell: function(data) {
          if (data.section !== 'body') return;
          
          const item = tableData[data.row.index];
          if (!item) return;
          
          if (item.type === 'grand_total') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [26, 54, 93];
            data.cell.styles.textColor = [255, 255, 255];
          } else if (item.type === 'state_header') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = item.state === 'TX' ? [219, 234, 254] : item.state === 'LA' ? [220, 252, 231] : [255, 237, 213];
          } else if (item.type === 'state_subtotal') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = item.state === 'TX' ? [191, 219, 254] : item.state === 'LA' ? [187, 247, 208] : [254, 215, 170];
          } else if (item.type === 'therapist' && data.column.index > 0) {
            const value = item.row[data.column.index];
            if (value > 0) {
              data.cell.styles.textColor = [220, 53, 69];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });
      
      doc.save(`missed_${unitLabel()}_weekly_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`Missed ${unitLabel()} report exported!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExpiringDocsPDF = async (days = 90) => {
    setExporting(true);
    try {
      // Get expiring documents for specified days, grouped by therapist
      const response = await axios.get(`${API}/reports/expiring-documents`, {
        params: { days }
      });
      const { expired_by_therapist, expiring_by_therapist, expired_count, expiring_soon_count } = response.data;
      
      if (expired_count === 0 && expiring_soon_count === 0) {
        toast.info(`No expiring or expired documents found within ${days} days`);
        setExporting(false);
        return;
      }
      
      const doc = newPortraitPdf();
      drawCompactHeader(doc, { title: 'Document Expiration Report', color: [0, 0, 0] });
      
      doc.setFontSize(9);
      doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 22);
      doc.text(`Looking ahead: ${days} days | Sorted by Therapist`, 14, 28);
      
      let currentY = 38;
      
      // Expired Documents Section - Grouped by Therapist
      if (expired_count > 0 && expired_by_therapist?.length > 0) {
        doc.setFontSize(12);
        doc.setTextColor(220, 53, 69);
        doc.text(`EXPIRED DOCUMENTS (${expired_count} documents, ${expired_by_therapist.length} therapists)`, 14, currentY);
        doc.setTextColor(0, 0, 0);
        currentY += 8;
        
        for (const therapist of expired_by_therapist) {
          // Check if we need a new page
          if (currentY > 250) {
            doc.addPage();
            currentY = 20;
          }
          
          // Therapist name header
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(therapist.therapist_name, 14, currentY);
          doc.setFont(undefined, 'normal');
          currentY += 5;
          
          // Documents table for this therapist
          const docsData = therapist.documents.map(d => [
            d.document_name + (d.is_missing ? ' [MISSING]' : ''),
            d.is_missing ? 'None' : new Date(d.expiration_date).toLocaleDateString(),
            d.is_missing ? 'Not uploaded' : `${Math.abs(d.days_until_expiry)} days ago`
          ]);
          
          autoTable(doc, {
            head: [['Document Name', 'Expired On', 'Status']],
            body: docsData,
            startY: currentY,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [220, 53, 69], fontStyle: 'bold' },
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 40 },
              2: { cellWidth: 40 }
            },
            margin: { left: 20 },
            didParseCell: function(data) {
              // Highlight missing credentials in purple
              if (data.section === 'body') {
                const docInfo = therapist.documents[data.row.index];
                if (docInfo?.is_missing) {
                  data.cell.styles.fillColor = [243, 232, 255]; // Light purple
                  data.cell.styles.textColor = [107, 33, 168]; // Purple text
                }
              }
            }
          });
          
          currentY = doc.lastAutoTable.finalY + 8;
        }
        
        currentY += 4;
      }
      
      // Expiring Soon Section - Grouped by Therapist
      if (expiring_soon_count > 0 && expiring_by_therapist?.length > 0) {
        if (currentY > 240) {
          doc.addPage();
          currentY = 20;
        }
        
        doc.setFontSize(12);
        doc.setTextColor(255, 140, 0);
        doc.text(`EXPIRING WITHIN ${days} DAYS (${expiring_soon_count} documents, ${expiring_by_therapist.length} therapists)`, 14, currentY);
        doc.setTextColor(0, 0, 0);
        currentY += 8;
        
        for (const therapist of expiring_by_therapist) {
          // Check if we need a new page
          if (currentY > 250) {
            doc.addPage();
            currentY = 20;
          }
          
          // Therapist name header
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(therapist.therapist_name, 14, currentY);
          doc.setFont(undefined, 'normal');
          currentY += 5;
          
          // Documents table for this therapist
          const docsData = therapist.documents.map(d => [
            d.document_name,
            new Date(d.expiration_date).toLocaleDateString(),
            `${d.days_until_expiry} days`
          ]);
          
          autoTable(doc, {
            head: [['Document Name', 'Expires On', 'Days Left']],
            body: docsData,
            startY: currentY,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [255, 140, 0], fontStyle: 'bold' },
            columnStyles: {
              0: { cellWidth: 80 },
              1: { cellWidth: 40 },
              2: { cellWidth: 40 }
            },
            margin: { left: 20 },
            didParseCell: function(data) {
              if (data.column.index === 2 && data.section === 'body') {
                const daysLeft = therapist.documents[data.row.index]?.days_until_expiry;
                if (daysLeft <= 7) {
                  data.cell.styles.textColor = [220, 53, 69]; // Red
                } else if (daysLeft <= 30) {
                  data.cell.styles.textColor = [255, 140, 0]; // Orange
                }
              }
            }
          });
          
          currentY = doc.lastAutoTable.finalY + 8;
        }
      }
      
      doc.save(`expiring_documents_${days}days_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`Expiring Documents (${days} days) report exported!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDateRangePreset = (preset) => {
    const today = new Date();
    let startDate, endDate;
    
    switch (preset) {
      case 'last_week':
        startDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last_2_weeks':
        startDate = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last_4_weeks':
        startDate = new Date(today.getTime() - 28 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last_6_weeks':
        startDate = new Date(today.getTime() - 42 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last_8_weeks':
        startDate = new Date(today.getTime() - 56 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'last_12_weeks':
        startDate = new Date(today.getTime() - 84 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }
    
    setFilters({
      ...filters,
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      date_range_preset: preset,
    });
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.profile_ids && filters.profile_ids.length > 0) {
        params.profile_id = filters.profile_ids.join(',');
      }
      if (filters.agency_names && filters.agency_names.length > 0) {
        params.agency = filters.agency_names.join(',');
      }
      if (filters.status_types && filters.status_types.length > 0) {
        params.status = filters.status_types.join(',');
      }
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await axios.get(`${API}/export`, {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'visits_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel export successful!');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      profile_ids: [],
      agency_names: [],
      status_types: [],
      start_date: '',
      end_date: '',
      date_range_preset: '',
    });
  };

  const hasActiveFilters = 
    (filters.profile_ids && filters.profile_ids.length > 0) ||
    (filters.agency_names && filters.agency_names.length > 0) ||
    (filters.status_types && filters.status_types.length > 0) ||
    (filters.task_names && filters.task_names.length > 0) ||
    filters.start_date !== '' || 
    filters.end_date !== '';

  // ==========================================
  // REPORT PREVIEW FUNCTIONS
  // ==========================================

  const loadWeeklySummaryPreview = async () => {
    setReportLoading(true);
    setReportPreview('weeklySummary');
    try {
      // Fetch projections and personnel
      const [projectionsRes, personnelRes, pendingKeys] = await Promise.all([
        axios.get(`${API}/projections`),
        axios.get(`${API}/personnel`),
        fetchPendingNameKeys(axios, API),
      ]);
      const allProjections = projectionsRes.data;
      const allPersonnel = personnelRes.data || [];
      
      // Shared resolver: linked profile id -> full name -> name_aliases (same as emailed PDF)
      const resolveTherapist = buildPersonnelResolver(allPersonnel);
      
      // Excluded task names - from tracking config or hardcoded fallback
      const excludedTaskNames = trackingConfig?.excluded_tasks || [
        'discharge summary (pt)', 'discharge summary (ot)', 'discharge summary (st)',
        'nb - supervisory visit (pt)', 'nb - supervisory visit (ot)',
        'nb hha sup visit (pt)',
        '5 day discharge notice ot', '5 day discharge notice pt', '5 day discharge notice st',
        'oasis-e1 discharge (non-billable) - ot', 'oasis-e1 discharge (non-billable) - pt',
        'oasis-e1 discharge (non-billable) - st',
        'case conference - nb (pt)', 'case conference - nb (ot)', 'case conference - nb (st)'
      ];
      
      // Visit weight function - uses tracking config when available
      const getVisitWeight = (visit) => getVisitWeightFromConfig(visit);
      
      // Calculate last 6 weeks IN THE PAST (ending with PREVIOUS Saturday)
      const now = new Date();
      const weeks = [];
      
      // Find the previous Saturday (not including today even if it's Saturday)
      const prevSaturday = new Date(now);
      const dayOfWeek = now.getDay();
      // Go back to the most recent past Saturday
      const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
      prevSaturday.setDate(now.getDate() - daysBack);
      
      // Build 6 weeks ending with previous Saturday
      for (let i = 5; i >= 0; i--) {
        const saturday = new Date(prevSaturday);
        saturday.setDate(prevSaturday.getDate() - (i * 7));
        saturday.setHours(23, 59, 59, 999); // End of day
        const sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() - 6);
        sunday.setHours(0, 0, 0, 0); // Start of day
        weeks.push({
          saturday: saturday,
          sundayDate: sunday,
          saturdayFormatted: `${(saturday.getMonth() + 1).toString().padStart(2, '0')}/${saturday.getDate().toString().padStart(2, '0')}`
        });
      }
      
      // For projections, find the LATEST available projection week from the data
      // (not hardcoded to "this Saturday" — the user may upload projections for any future week)
      let latestProj1Week = null;
      let latestProj2Week = null;
      allProjections.forEach(proj => {
        if (proj.projection_type === 'Proj 1' && proj.week_ending) {
          if (!latestProj1Week || proj.week_ending > latestProj1Week) latestProj1Week = proj.week_ending;
        }
        if (proj.projection_type === 'Proj 2' && proj.week_ending) {
          if (!latestProj2Week || proj.week_ending > latestProj2Week) latestProj2Week = proj.week_ending;
        }
      });
      const currentSaturday = new Date(now);
      if (dayOfWeek !== 6) {
        currentSaturday.setDate(now.getDate() + (6 - dayOfWeek));
      }
      const fallbackWeek = `${currentSaturday.getFullYear()}-${(currentSaturday.getMonth() + 1).toString().padStart(2, '0')}-${currentSaturday.getDate().toString().padStart(2, '0')}`;
      const projectionWeekEnding = latestProj1Week || fallbackWeek;
      
      // Get unique therapists from visits with weighted counting
      const therapistMap = {};
      visits.forEach(visit => {
        const row = resolveTherapistRow(resolveTherapist, {
          profileId: visit.profile_id || visit.therapist_id,
          name: `${visit.first_name} ${visit.last_name}`,
        }, pendingKeys);
        const normalizedKey = row.key;
        if (!therapistMap[normalizedKey]) {
          therapistMap[normalizedKey] = { ...row, last_name: row.last_name || visit.last_name || '', weeks: {}, proj1: 0, proj2: 0 };
        }
        
        // Get visit weight
        const weight = getVisitWeight(visit);
        if (weight === 0) return; // Skip excluded visits
        
        // Parse visit date (handles MM/DD/YYYY format)
        let visitDate;
        const dateStr = visit.date || visit.created_at;
        if (dateStr && dateStr.includes('/')) {
          // MM/DD/YYYY format
          const [month, day, year] = dateStr.split('/');
          visitDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0); // Noon to avoid timezone issues
        } else {
          visitDate = new Date(dateStr);
        }
        
        // Determine which week this visit belongs to (only count once)
        for (let idx = 0; idx < weeks.length; idx++) {
          const week = weeks[idx];
          if (visitDate >= week.sundayDate && visitDate <= week.saturday) {
            therapistMap[normalizedKey].weeks[idx] = (therapistMap[normalizedKey].weeks[idx] || 0) + weight;
            break; // Only count in one week
          }
        }
      });
      
      // Add projection data for the upcoming/current week
      console.log('Looking for projections with week_ending:', projectionWeekEnding);
      console.log('Available projection week_endings:', [...new Set(allProjections.map(p => p.week_ending))]);
      console.log('Total projections received:', allProjections.length);
      
      let matchedProjections = 0;
      allProjections.forEach(proj => {
        // Match Proj 1 by latestProj1Week, Proj 2 by latestProj2Week
        const matchWeek = proj.projection_type === 'Proj 2' ? latestProj2Week : projectionWeekEnding;
        if (proj.week_ending === matchWeek) {
          matchedProjections++;
          const row = resolveTherapistRow(resolveTherapist, {
            profileId: proj.personnel_id || proj.profile_id || proj.therapist_id,
            name: proj.therapist_name,
          }, pendingKeys);
          const normalizedKey = row.key;
          if (!therapistMap[normalizedKey]) {
            therapistMap[normalizedKey] = { ...row, weeks: {}, proj1: 0, proj2: 0 };
          }
          
          // Calculate weighted projection count from visit_details
          let weightedCount = 0;
          const visitDetails = proj.visit_details || [];
          visitDetails.forEach(visit => {
            // Use the same weight function as actual visits for consistency
            const weight = getVisitWeightFromConfig({
              task_name: visit.visit_name,
              status: visit.status
            });
            weightedCount += weight;
          });
          
          // ADD weighted count (therapist may have multiple projection rows)
          if (proj.projection_type === 'Proj 1') {
            therapistMap[normalizedKey].proj1 += weightedCount;
          } else if (proj.projection_type === 'Proj 2') {
            therapistMap[normalizedKey].proj2 += weightedCount;
          }
        }
      });
      console.log('Matched projections:', matchedProjections);
      
      // Custom state order: TX first, then LA, then others alphabetically
      const getStateOrder = (state) => {
        const s = (state || 'ZZZ').toUpperCase();
        if (s === 'TX') return '0_TX';
        if (s === 'LA') return '1_LA';
        if (s === 'ZZZ' || s === 'OTHER') return '9_' + s;
        return '2_' + s; // Other states come after TX/LA but before empty/Other
      };
      
      // Convert to array and sort by STATE first (TX, LA, others), then by LAST NAME
      const therapistsData = Object.values(therapistMap).sort((a, b) => {
        const stateA = getStateOrder(a.state);
        const stateB = getStateOrder(b.state);
        if (stateA !== stateB) return stateA.localeCompare(stateB);
        return (a.last_name || '').localeCompare(b.last_name || '');
      });
      
      // Calculate grand totals (for top of report)
      const weekTotals = weeks.map((_, idx) => 
        therapistsData.reduce((sum, t) => sum + (t.weeks[idx] || 0), 0)
      );
      const proj1Total = therapistsData.reduce((sum, t) => sum + (t.proj1 || 0), 0);
      const proj2Total = therapistsData.reduce((sum, t) => sum + (t.proj2 || 0), 0);
      
      // Calculate state subtotals
      const stateSubtotals = {};
      therapistsData.forEach(t => {
        const state = t.state || 'Other';
        if (!stateSubtotals[state]) {
          stateSubtotals[state] = {
            state: state,
            weekTotals: weeks.map(() => 0),
            proj1Total: 0,
            proj2Total: 0
          };
        }
        weeks.forEach((_, idx) => {
          stateSubtotals[state].weekTotals[idx] += (t.weeks[idx] || 0);
        });
        stateSubtotals[state].proj1Total += (t.proj1 || 0);
        stateSubtotals[state].proj2Total += (t.proj2 || 0);
      });
      
      // Get ordered list of states as they appear in sorted data
      const stateOrder = [];
      therapistsData.forEach(t => {
        const state = t.state || 'Other';
        if (!stateOrder.includes(state)) {
          stateOrder.push(state);
        }
      });
      
      console.log('therapistsData count:', therapistsData.length);
      console.log('proj1Total calculated:', proj1Total);
      console.log('States found:', stateOrder);
      
      // Check if we have projections
      const hasProj1 = proj1Total > 0;
      const hasProj2 = proj2Total > 0;
      
      setReportData({ 
        weeks, 
        therapists: therapistsData, 
        weekTotals,
        proj1Total,
        proj2Total,
        hasProj1,
        hasProj2,
        stateSubtotals,
        stateOrder
      });
    } catch (error) {
      console.error('Weekly summary error:', error);
      toast.error('Failed to load report');
      setReportPreview(null);
    } finally {
      setReportLoading(false);
    }
  };

  const loadWeeklyAgencySummaryPreview = async () => {
    setReportLoading(true);
    setReportPreview('weeklyAgency');
    try {
      // Fetch projections and agency profiles for state info
      const [projectionsRes, agencyProfilesRes] = await Promise.all([
        axios.get(`${API}/projections`),
        axios.get(`${API}/agency-profiles`)
      ]);
      const allProjections = projectionsRes.data;
      const allAgencyProfiles = agencyProfilesRes.data;
      
      // Create a map of agency name to state (case-insensitive, trimmed)
      const agencyStateMap = {};
      const agencyIdMap = {}; // Also store ID for updates
      allAgencyProfiles.forEach(a => {
        const normalizedName = (a.agency_name || '').toLowerCase().trim();
        agencyStateMap[normalizedName] = a.state || 'Other';
        agencyIdMap[normalizedName] = a.id;
      });
      
      // Helper to get state for an agency (case-insensitive lookup)
      const getAgencyState = (agencyName) => {
        const normalized = (agencyName || '').toLowerCase().trim();
        return agencyStateMap[normalized] || 'Other';
      };
      
      // Excluded task names - from tracking config or hardcoded fallback
      const excludedTaskNames = trackingConfig?.excluded_tasks || [
        'discharge summary (pt)', 'discharge summary (ot)', 'discharge summary (st)',
        'nb - supervisory visit (pt)', 'nb - supervisory visit (ot)',
        'nb hha sup visit (pt)',
        '5 day discharge notice ot', '5 day discharge notice pt', '5 day discharge notice st',
        'oasis-e1 discharge (non-billable) - ot', 'oasis-e1 discharge (non-billable) - pt',
        'oasis-e1 discharge (non-billable) - st',
        'case conference - nb (pt)', 'case conference - nb (ot)', 'case conference - nb (st)'
      ];
      
      // Visit weight function - uses tracking config when available
      const getVisitWeight = (visit) => getVisitWeightFromConfig(visit);
      
      // Calculate last 6 weeks IN THE PAST
      const now = new Date();
      const weeks = [];
      const dayOfWeek = now.getDay();
      const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
      const prevSaturday = new Date(now);
      prevSaturday.setDate(now.getDate() - daysBack);
      
      for (let i = 5; i >= 0; i--) {
        const saturday = new Date(prevSaturday);
        saturday.setDate(prevSaturday.getDate() - (i * 7));
        saturday.setHours(23, 59, 59, 999);
        const sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() - 6);
        sunday.setHours(0, 0, 0, 0);
        weeks.push({
          saturday: saturday,
          sundayDate: sunday,
          saturdayFormatted: `${(saturday.getMonth() + 1).toString().padStart(2, '0')}/${saturday.getDate().toString().padStart(2, '0')}`
        });
      }
      
      // For projections, find the LATEST available projection week from the data
      let latestProj1Week = null;
      let latestProj2Week = null;
      allProjections.forEach(proj => {
        if (proj.projection_type === 'Proj 1' && proj.week_ending) {
          if (!latestProj1Week || proj.week_ending > latestProj1Week) latestProj1Week = proj.week_ending;
        }
        if (proj.projection_type === 'Proj 2' && proj.week_ending) {
          if (!latestProj2Week || proj.week_ending > latestProj2Week) latestProj2Week = proj.week_ending;
        }
      });
      const currentSaturday = new Date(now);
      if (dayOfWeek !== 6) {
        currentSaturday.setDate(now.getDate() + (6 - dayOfWeek));
      }
      const fallbackWeek = `${currentSaturday.getFullYear()}-${(currentSaturday.getMonth() + 1).toString().padStart(2, '0')}-${currentSaturday.getDate().toString().padStart(2, '0')}`;
      const projectionWeekEnding = latestProj1Week || fallbackWeek;
      
      // Group by AGENCY instead of therapist
      const agencyMap = {};
      visits.forEach(visit => {
        const agencyKey = visit.agency || 'Unknown';
        if (!agencyMap[agencyKey]) {
          agencyMap[agencyKey] = {
            name: agencyKey,
            state: getAgencyState(agencyKey),
            weeks: {},
            proj1: 0,
            proj2: 0
          };
        }
        
        const weight = getVisitWeight(visit);
        if (weight === 0) return;
        
        let visitDate;
        const dateStr = visit.date || visit.created_at;
        if (dateStr && dateStr.includes('/')) {
          const [month, day, year] = dateStr.split('/');
          visitDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
        } else {
          visitDate = new Date(dateStr);
        }
        
        for (let idx = 0; idx < weeks.length; idx++) {
          const week = weeks[idx];
          if (visitDate >= week.sundayDate && visitDate <= week.saturday) {
            agencyMap[agencyKey].weeks[idx] = (agencyMap[agencyKey].weeks[idx] || 0) + weight;
            break;
          }
        }
      });
      
      // Add projection data grouped by agency
      allProjections.forEach(proj => {
        // Match Proj 1 by latestProj1Week, Proj 2 by latestProj2Week
        const matchWeek = proj.projection_type === 'Proj 2' ? latestProj2Week : projectionWeekEnding;
        if (proj.week_ending === matchWeek) {
          const agencyKey = proj.agency || 'Unknown';
          if (!agencyMap[agencyKey]) {
            agencyMap[agencyKey] = {
              name: agencyKey,
              state: getAgencyState(agencyKey),
              weeks: {},
              proj1: 0,
              proj2: 0
            };
          }
          
          let weightedCount = 0;
          const visitDetails = proj.visit_details || [];
          visitDetails.forEach(visit => {
            // Use the same weight function as actual visits for consistency
            const weight = getVisitWeightFromConfig({
              task_name: visit.visit_name || visit.task_name,
              status: visit.status
            });
            weightedCount += weight;
          });
          
          if (proj.projection_type === 'Proj 1') {
            agencyMap[agencyKey].proj1 += weightedCount;
          } else if (proj.projection_type === 'Proj 2') {
            agencyMap[agencyKey].proj2 += weightedCount;
          }
        }
      });
      
      // Filter to only TX and LA agencies for display, track missing states
      const filteredAgencyMap = {};
      const agenciesWithMissingState = [];
      
      Object.entries(agencyMap).forEach(([key, agency]) => {
        const state = (agency.state || '').toUpperCase();
        if (state === 'TX' || state === 'LA') {
          filteredAgencyMap[key] = agency;
        } else if (agency.proj1 > 0 || agency.proj2 > 0) {
          // Only track if they have projected visits
          agenciesWithMissingState.push({
            name: key,
            type: 'agency',
            proj1: agency.proj1,
            proj2: agency.proj2,
            currentState: agency.state || 'Not set'
          });
          // Still include in filtered for now
          filteredAgencyMap[key] = agency;
        }
      });
      
      // If there are agencies with missing states, prompt user
      if (agenciesWithMissingState.length > 0) {
        setMissingStateItems(agenciesWithMissingState);
        setPendingReportType('agency');
        setShowMissingStateModal(true);
        setReportLoading(false);
        return;
      }
      
      // Custom state order: TX first, then LA
      const getStateOrder = (state) => {
        const s = (state || 'ZZZ').toUpperCase();
        if (s === 'TX') return '0_TX';
        if (s === 'LA') return '1_LA';
        return '9_' + s;
      };
      
      // Sort agencies by STATE first (TX, LA), then by NAME
      const agenciesData = Object.values(filteredAgencyMap).sort((a, b) => {
        const stateA = getStateOrder(a.state);
        const stateB = getStateOrder(b.state);
        if (stateA !== stateB) return stateA.localeCompare(stateB);
        return a.name.localeCompare(b.name);
      });
      
      // Calculate grand totals from ALL agencies (not just filtered, to match dashboard)
      const allAgenciesData = Object.values(agencyMap);
      const weekTotals = weeks.map((_, idx) => 
        allAgenciesData.reduce((sum, a) => sum + (a.weeks[idx] || 0), 0)
      );
      const proj1Total = allAgenciesData.reduce((sum, a) => sum + (a.proj1 || 0), 0);
      const proj2Total = allAgenciesData.reduce((sum, a) => sum + (a.proj2 || 0), 0);
      
      // Calculate state subtotals
      const stateSubtotals = {};
      agenciesData.forEach(a => {
        const state = a.state || 'Other';
        if (!stateSubtotals[state]) {
          stateSubtotals[state] = {
            state: state,
            weekTotals: weeks.map(() => 0),
            proj1Total: 0,
            proj2Total: 0
          };
        }
        weeks.forEach((_, idx) => {
          stateSubtotals[state].weekTotals[idx] += (a.weeks[idx] || 0);
        });
        stateSubtotals[state].proj1Total += (a.proj1 || 0);
        stateSubtotals[state].proj2Total += (a.proj2 || 0);
      });
      
      // Get ordered list of states
      const stateOrder = [];
      agenciesData.forEach(a => {
        const state = a.state || 'Other';
        if (!stateOrder.includes(state)) {
          stateOrder.push(state);
        }
      });
      
      const hasProj1 = proj1Total > 0;
      const hasProj2 = proj2Total > 0;
      
      setReportData({ 
        weeks, 
        agencies: agenciesData, 
        weekTotals,
        proj1Total,
        proj2Total,
        hasProj1,
        hasProj2,
        stateSubtotals,
        stateOrder
      });
    } catch (error) {
      console.error('Weekly agency summary error:', error);
      toast.error('Failed to load report');
      setReportPreview(null);
    } finally {
      setReportLoading(false);
    }
  };
  
  const loadEvalDischargePreview = async () => {
    setReportLoading(true);
    setReportPreview('evalDischarge');
    try {
      const response = await axios.get(`${API}/projections/eval-discharge`);
      const data = response.data;
      if (!data || data.length === 0) {
        toast.info('No eval or discharge data found');
        setReportPreview(null);
        return;
      }
      // Filter out Unknown agencies first, then calculate totals
      const filteredData = data.filter(d => d.agency !== 'Unknown');
      const totals = filteredData.reduce((acc, row) => {
        acc.evals += row.evals;
        acc.discharges += row.discharges;
        acc.difference += row.difference;
        return acc;
      }, { evals: 0, discharges: 0, difference: 0 });
      setReportData({ items: filteredData, totals, weekEnding: data[0]?.week_ending, projType: data[0]?.projection_type_used });
    } catch (error) {
      toast.error('Failed to load report');
      setReportPreview(null);
    } finally {
      setReportLoading(false);
    }
  };

  const loadLateNotesPreview = async () => {
    setReportLoading(true);
    setReportPreview('lateNotes');
    try {
      const response = await axios.get(`${API}/reports/late-notes`);
      const { week_dates, therapists } = response.data;
      if (!therapists || therapists.length === 0) {
        toast.info('No late notes found for the last 6 weeks');
        setReportPreview(null);
        return;
      }
      // Convert weeks object to array for each therapist
      const therapistsWithArrays = therapists.map(t => ({
        ...t,
        weekly_counts: week_dates.map(date => t.weeks?.[date] || 0)
      }));
      
      // Group by state
      const txTherapists = therapistsWithArrays.filter(t => t.state === 'TX');
      const laTherapists = therapistsWithArrays.filter(t => t.state === 'LA');
      const otherTherapists = therapistsWithArrays.filter(t => t.state !== 'TX' && t.state !== 'LA');
      
      // Calculate totals per week for each state
      const txWeekTotals = week_dates.map((date) => 
        txTherapists.reduce((sum, t) => sum + (t.weeks?.[date] || 0), 0)
      );
      const laWeekTotals = week_dates.map((date) => 
        laTherapists.reduce((sum, t) => sum + (t.weeks?.[date] || 0), 0)
      );
      
      // Calculate totals per week
      const weekTotals = week_dates.map((date) => 
        therapists.reduce((sum, t) => sum + (t.weeks?.[date] || 0), 0)
      );
      const grandTotal = weekTotals.reduce((a, b) => a + b, 0);
      const txTotal = txWeekTotals.reduce((a, b) => a + b, 0);
      const laTotal = laWeekTotals.reduce((a, b) => a + b, 0);
      
      setReportData({ 
        weekDates: week_dates, 
        therapists: therapistsWithArrays, 
        txTherapists,
        laTherapists,
        otherTherapists,
        weekTotals, 
        txWeekTotals,
        laWeekTotals,
        grandTotal,
        txTotal,
        laTotal
      });
    } catch (error) {
      console.error('Late notes error:', error);
      toast.error('Failed to load report');
      setReportPreview(null);
    } finally {
      setReportLoading(false);
    }
  };

  const loadMissedVisitsPreview = async () => {
    setReportLoading(true);
    setReportPreview('missedVisits');
    try {
      const response = await axios.get(`${API}/reports/missed-visits`);
      const { week_dates, therapists } = response.data;
      if (!therapists || therapists.length === 0) {
        toast.info(`No missed ${unitLabel()} found for the last 6 weeks`);
        setReportPreview(null);
        return;
      }
      // Convert weeks object to array for each therapist
      const therapistsWithArrays = therapists.map(t => ({
        ...t,
        weekly_counts: week_dates.map(date => t.weeks?.[date] || 0)
      }));
      
      // Custom state order: TX first, then LA, then others alphabetically
      const getStateOrder = (state) => {
        const s = (state || 'ZZZ').toUpperCase();
        if (s === 'TX') return '0_TX';
        if (s === 'LA') return '1_LA';
        if (s === 'ZZZ' || s === 'OTHER') return '9_' + s;
        return '2_' + s;
      };
      
      // Sort by state then last name
      therapistsWithArrays.sort((a, b) => {
        const stateA = getStateOrder(a.state);
        const stateB = getStateOrder(b.state);
        if (stateA !== stateB) return stateA.localeCompare(stateB);
        return (a.last_name || '').localeCompare(b.last_name || '');
      });
      
      // Build state order and group therapists by state
      const stateOrder = [];
      const stateGroups = {};
      therapistsWithArrays.forEach(t => {
        const state = t.state || 'Other';
        if (!stateOrder.includes(state)) stateOrder.push(state);
        if (!stateGroups[state]) stateGroups[state] = [];
        stateGroups[state].push(t);
      });
      
      // Calculate week totals per state
      const stateWeekTotals = {};
      const stateTotals = {};
      stateOrder.forEach(state => {
        const group = stateGroups[state];
        stateWeekTotals[state] = week_dates.map((date, idx) =>
          group.reduce((sum, t) => sum + (t.weekly_counts[idx] || 0), 0)
        );
        stateTotals[state] = stateWeekTotals[state].reduce((a, b) => a + b, 0);
      });
      
      // Grand totals
      const weekTotals = week_dates.map((_, idx) => 
        therapistsWithArrays.reduce((sum, t) => sum + (t.weekly_counts[idx] || 0), 0)
      );
      const grandTotal = weekTotals.reduce((a, b) => a + b, 0);
      
      setReportData({ 
        weekDates: week_dates, 
        therapists: therapistsWithArrays, 
        stateOrder,
        stateGroups,
        stateWeekTotals,
        stateTotals,
        weekTotals, 
        grandTotal
      });
    } catch (error) {
      console.error('Missed visits error:', error);
      toast.error('Failed to load report');
      setReportPreview(null);
    } finally {
      setReportLoading(false);
    }
  };

  const loadExpiringDocsPreview = async (days) => {
    setReportLoading(true);
    setExpiringDocsDays(days);
    setReportPreview('expiringDocs');
    try {
      const response = await axios.get(`${API}/reports/expiring-documents?days=${days}`);
      const data = response.data;
      
      // Flatten the grouped data into flat arrays for display
      const expired = [];
      const expiring = [];
      
      // Process expired documents (including missing credentials)
      (data.expired_by_therapist || []).forEach(therapist => {
        (therapist.documents || []).forEach(doc => {
          expired.push({
            therapist_name: therapist.therapist_name,
            discipline: therapist.discipline,
            document_name: doc.document_name,
            expiration_date: doc.expiration_date,
            days_until_expiry: doc.days_until_expiry,
            is_missing: doc.is_missing || false
          });
        });
      });
      
      // Process expiring documents
      (data.expiring_by_therapist || []).forEach(therapist => {
        (therapist.documents || []).forEach(doc => {
          expiring.push({
            therapist_name: therapist.therapist_name,
            discipline: therapist.discipline,
            document_name: doc.document_name,
            expiration_date: doc.expiration_date,
            days_until_expiry: doc.days_until_expiry,
            is_missing: doc.is_missing || false
          });
        });
      });
      
      if (expired.length === 0 && expiring.length === 0) {
        toast.info(`No documents expiring within ${days} days`);
        setReportPreview(null);
        return;
      }
      
      setReportData({ 
        expired, 
        expiring, 
        days,
        missing_count: data.missing_credentials_count || 0,
        required_credentials: data.required_credentials || []
      });
    } catch (error) {
      toast.error('Failed to load report');
      setReportPreview(null);
    } finally {
      setReportLoading(false);
    }
  };

  const closeReportPreview = () => {
    setReportPreview(null);
    setReportData(null);
  };


  // ---- Email Reports ---------------------------------------------------- //
  const handleSendEmailReports = async () => {
    const recipients = emailRecipients.split(/[,;\s]+/).map(e => e.trim()).filter(e => e.includes('@'));
    if (recipients.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }
    const selectedReports = Object.entries(emailReports).filter(([, v]) => v).map(([k]) => k);
    if (selectedReports.length === 0) {
      toast.error('Please select at least one report');
      return;
    }

    setEmailSending(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch data needed for PDFs and metrics
      const [visitsRes, configRes, projectionsRes, agenciesRes, profilesRes, personnelRes] = await Promise.all([
        axios.get(`${API}/visits`, { headers }),
        axios.get(`${API}/tracking-config`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/projections`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/agency-profiles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/personnel`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/personnel`, { headers }).catch(() => ({ data: [] })),
      ]);

      const allVisits = visitsRes.data || [];
      const config = configRes.data;
      const allProjections = projectionsRes.data || [];
      const allAgencies = agenciesRes.data || [];

      // Compute weekly metrics for the email body
      const metrics = computeWeeklyMetrics(allVisits, allProjections, config, allAgencies);

      // Build the final week date for subject (full date MM/DD/YYYY)
      const weDate = new Date(metrics.week_ending + 'T00:00:00');
      const fullDateLabel = `${String(weDate.getMonth()+1).padStart(2,'0')}/${String(weDate.getDate()).padStart(2,'0')}/${weDate.getFullYear()}`;
      const subject = `Weekly Reports ${fullDateLabel}`;
      const weekLabel = fullDateLabel;

      // Build metrics HTML for email body
      // Custom user message (if provided) is rendered as a styled block above the metrics.
      const escapeHtml = (s) => String(s || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const messageHtml = (emailMessage || '').trim()
        ? `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto 16px;padding:16px;background:#f1f5f9;border-left:4px solid #1a3a5c;border-radius:4px;color:#222;white-space:pre-wrap;font-size:14px;line-height:1.55;">${escapeHtml(emailMessage.trim())}</div>`
        : '';
      const metricsHtml = messageHtml + `
        <div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;">
          <h2 style="color:#1a3a5c;border-bottom:2px solid #1a3a5c;padding-bottom:8px;">Weekly Metrics — Week Ending ${weekLabel}</h2>
          <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
            <tr style="background:#f1f5f9;"><td style="padding:8px;border:1px solid #e2e8f0;"><strong>Assistcare/Happy at Home:</strong></td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;"><strong>${metrics.assistcare_happy_at_home}</strong></td></tr>
            <tr><td style="padding:8px;border:1px solid #e2e8f0;"><strong>All other agencies:</strong></td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;"><strong>${metrics.all_other_agencies}</strong></td></tr>
          </table>
          <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
            <tr style="background:#f1f5f9;"><td style="padding:8px;border:1px solid #e2e8f0;"><strong>Final:</strong></td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;"><strong>${metrics.final}</strong></td></tr>
            <tr><td style="padding:8px;border:1px solid #e2e8f0;"><strong>Proj 1:</strong></td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;"><strong>${metrics.proj_1}</strong></td></tr>
            <tr style="background:#f1f5f9;"><td style="padding:8px;border:1px solid #e2e8f0;"><strong>Last Week Proj 1:</strong></td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;"><strong>${metrics.last_week_proj_1}</strong></td></tr>
          </table>
          <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
            <tr style="background:#e2e8f0;"><td colspan="2" style="padding:8px;border:1px solid #cbd5e1;"><strong>Agency Evals and Discharges</strong></td></tr>
            <tr><td style="padding:8px;border:1px solid #e2e8f0;">Total Evals:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.total_evals}</td></tr>
            <tr style="background:#f1f5f9;"><td style="padding:8px;border:1px solid #e2e8f0;">Total Discharges:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.total_discharges}</td></tr>
            <tr><td style="padding:8px;border:1px solid #e2e8f0;">Difference:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.difference >= 0 ? '+' : ''}${metrics.difference}</td></tr>
          </table>
          <table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
            <tr style="background:#e2e8f0;"><td colspan="2" style="padding:8px;border:1px solid #cbd5e1;"><strong>Missed Visits</strong></td></tr>
            <tr><td style="padding:8px;border:1px solid #e2e8f0;">MV Percentage:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.mv_percentage}%</td></tr>
            <tr style="background:#f1f5f9;"><td style="padding:8px;border:1px solid #e2e8f0;">Total Notes Completed:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.total_notes}</td></tr>
            <tr><td style="padding:8px;border:1px solid #e2e8f0;">Total MV:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.total_mv}</td></tr>
            <tr style="background:#f1f5f9;"><td style="padding:8px;border:1px solid #e2e8f0;">MVs for Louisiana:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.mv_louisiana}</td></tr>
            <tr><td style="padding:8px;border:1px solid #e2e8f0;">MVs for Texas:</td><td style="padding:8px;border:1px solid #e2e8f0;text-align:right;">${metrics.mv_texas}</td></tr>
          </table>
          <p style="color:#94a3b8;font-size:12px;">Reports are attached as PDF files.</p>
        </div>`;

      // Generate PDFs for selected reports
      const attachments = [];
      const dateStr = new Date().toISOString().split('T')[0];

      // Helper: generate PDF and return as base64
      const pdfToBase64 = (doc) => {
        const arrayBuffer = doc.output('arraybuffer');
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
      };

      // Generate each selected report
      for (const reportKey of selectedReports) {
        try {
          if (reportKey === 'therapists') {
            // Trigger the load and use existing PDF generator logic
            const doc = await generateTherapistsPDF(allVisits, allProjections, allAgencies, profilesRes.data, personnelRes.data, config);
            if (doc) attachments.push({ filename: `therapists_weekly_${dateStr}.pdf`, data_base64: pdfToBase64(doc) });
          } else if (reportKey === 'agencies') {
            const doc = await generateAgenciesPDF(allVisits, allProjections, allAgencies, config);
            if (doc) attachments.push({ filename: `agencies_weekly_${dateStr}.pdf`, data_base64: pdfToBase64(doc) });
          } else if (reportKey === 'evalDc') {
            const res = await axios.get(`${API}/projections/eval-discharge`, { headers });
            const doc = generateEvalDcPDF(res.data);
            if (doc) attachments.push({ filename: `eval_vs_discharge_${dateStr}.pdf`, data_base64: pdfToBase64(doc) });
          } else if (reportKey === 'lateNotes') {
            const res = await axios.get(`${API}/reports/late-notes`, { headers });
            const doc = generateLateNotesPDF(res.data);
            if (doc) attachments.push({ filename: `late_notes_${dateStr}.pdf`, data_base64: pdfToBase64(doc) });
          } else if (reportKey === 'missed') {
            const res = await axios.get(`${API}/reports/missed-visits`, { headers });
            const doc = generateMissedVisitsPDF(res.data);
            if (doc) attachments.push({ filename: `missed_visits_${dateStr}.pdf`, data_base64: pdfToBase64(doc) });
          }
        } catch (err) {
          console.error(`Failed to generate ${reportKey} PDF:`, err);
        }
      }

      // Send email
      await axios.post(`${API}/reports/email`, {
        recipients,
        subject,
        metrics_html: metricsHtml,
        attachments,
      }, { headers });

      toast.success(`Reports emailed to ${recipients.length} recipient(s)`);
      setShowEmailModal(false);
    } catch (error) {
      console.error('Email reports error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  // Standalone PDF generators for email (don't save, return doc object)
  const generateTherapistsPDF = async (allVisits, allProjections, allAgencies, allProfiles, allPersonnel, config) => {
    try {
      const getWeight = (visit) => sharedGetVisitWeight(visit.task_name, visit.status, config);

      // Same resolver as the on-screen preview (personnel first, legacy profiles as fallback)
      const resolveTherapist = buildPersonnelResolver([...(allPersonnel || []), ...(allProfiles || [])]);
      const pendingKeys = await fetchPendingNameKeys(axios, API);

      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
      const prevSaturday = new Date(now);
      prevSaturday.setDate(now.getDate() - daysBack);

      const weeks = [];
      for (let i = 5; i >= 0; i--) {
        const saturday = new Date(prevSaturday);
        saturday.setDate(prevSaturday.getDate() - (i * 7));
        saturday.setHours(23, 59, 59, 999);
        const sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() - 6);
        sunday.setHours(0, 0, 0, 0);
        weeks.push({ saturday, sundayDate: sunday, saturdayFormatted: `${(saturday.getMonth()+1).toString().padStart(2,'0')}/${saturday.getDate().toString().padStart(2,'0')}` });
      }

      let latestProj1Week = null;
      allProjections.forEach(p => { if (p.projection_type === 'Proj 1' && p.week_ending && (!latestProj1Week || p.week_ending > latestProj1Week)) latestProj1Week = p.week_ending; });
      const projWeek = latestProj1Week || '';

      const therapistMap = {};
      allVisits.forEach(v => {
        const row = resolveTherapistRow(resolveTherapist, { profileId: v.profile_id || v.therapist_id, name: `${v.first_name} ${v.last_name}` }, pendingKeys);
        const key = row.key;
        if (!therapistMap[key]) {
          therapistMap[key] = { ...row, last_name: row.last_name || v.last_name || '', weeks: {}, proj1: 0 };
        }
        const weight = getWeight(v);
        if (weight === 0) return;
        const dateStr = v.date || '';
        let visitDate;
        if (dateStr.includes('/')) { const [m,d,y] = dateStr.split('/'); visitDate = new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12); }
        else visitDate = new Date(dateStr);
        for (let idx = 0; idx < weeks.length; idx++) {
          if (visitDate >= weeks[idx].sundayDate && visitDate <= weeks[idx].saturday) {
            therapistMap[key].weeks[idx] = (therapistMap[key].weeks[idx] || 0) + weight;
            break;
          }
        }
      });

      allProjections.forEach(proj => {
        if (proj.week_ending !== projWeek || proj.projection_type !== 'Proj 1') return;
        const row = resolveTherapistRow(resolveTherapist, { profileId: proj.personnel_id || proj.profile_id || proj.therapist_id, name: proj.therapist_name }, pendingKeys);
        const key = row.key;
        if (!therapistMap[key]) {
          therapistMap[key] = { ...row, weeks: {}, proj1: 0 };
        }
        let wc = 0;
        (proj.visit_details || []).forEach(d => { wc += sharedGetVisitWeight(d.visit_name, d.status, config); });
        therapistMap[key].proj1 += wc;
      });

      const stateOrder = (s) => { const u = (s||'').toUpperCase(); return u === 'TX' ? '0' : u === 'LA' ? '1' : '2_'+u; };
      const data = Object.values(therapistMap).sort((a,b) => stateOrder(a.state).localeCompare(stateOrder(b.state)) || (a.last_name||'').localeCompare(b.last_name||''));

      const doc = newLandscapePdf();
      drawCompactHeader(doc, { title: 'Therapists Weekly', subtitle: `Week Ending: ${weeks[weeks.length-1].saturdayFormatted}` });

      const headers = ['Therapist', 'State', 'Disc.', ...weeks.map(w => w.saturdayFormatted), 'Proj 1'];
      const rows = data.map(t => [withInactive(t.name), t.state, t.discipline, ...weeks.map((_,i) => t.weeks[i] || '-'), t.proj1 || '-']);
      const totals = ['GRAND TOTAL', '', '', ...weeks.map((_,i) => data.reduce((s,t) => s + (t.weeks[i]||0), 0)), data.reduce((s,t) => s + (t.proj1||0), 0)];
      rows.unshift(totals);

      autoTable(doc, { head: [headers], body: rows, startY: 22, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [26,54,93], textColor: 255 } });
      return doc;
    } catch (e) { console.error('Therapists PDF gen error:', e); return null; }
  };

  const generateAgenciesPDF = async (allVisits, allProjections, allAgencies, config) => {
    try {
      const getWeight = (visit) => sharedGetVisitWeight(visit.task_name, visit.status, config);
      const agencyStateMap = {};
      allAgencies.forEach(a => { agencyStateMap[(a.agency_name||'').toLowerCase().trim()] = a.state || 'Other'; });

      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
      const prevSaturday = new Date(now);
      prevSaturday.setDate(now.getDate() - daysBack);
      const weeks = [];
      for (let i = 5; i >= 0; i--) {
        const sat = new Date(prevSaturday); sat.setDate(prevSaturday.getDate() - (i*7)); sat.setHours(23,59,59,999);
        const sun = new Date(sat); sun.setDate(sat.getDate()-6); sun.setHours(0,0,0,0);
        weeks.push({ saturday: sat, sundayDate: sun, saturdayFormatted: `${(sat.getMonth()+1).toString().padStart(2,'0')}/${sat.getDate().toString().padStart(2,'0')}` });
      }

      let latestProj1Week = null;
      allProjections.forEach(p => { if (p.projection_type === 'Proj 1' && p.week_ending && (!latestProj1Week || p.week_ending > latestProj1Week)) latestProj1Week = p.week_ending; });
      const projWeek = latestProj1Week || '';

      const agencyMap = {};
      allVisits.forEach(v => {
        const key = v.agency || 'Unknown';
        if (!agencyMap[key]) agencyMap[key] = { name: key, state: agencyStateMap[(key||'').toLowerCase().trim()] || 'Other', weeks: {}, proj1: 0 };
        const w = getWeight(v);
        if (w === 0) return;
        const ds = v.date || '';
        let vd;
        if (ds.includes('/')) { const [m,d,y] = ds.split('/'); vd = new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12); } else vd = new Date(ds);
        for (let idx = 0; idx < weeks.length; idx++) { if (vd >= weeks[idx].sundayDate && vd <= weeks[idx].saturday) { agencyMap[key].weeks[idx] = (agencyMap[key].weeks[idx]||0) + w; break; } }
      });

      allProjections.forEach(proj => {
        if (proj.week_ending !== projWeek || proj.projection_type !== 'Proj 1') return;
        const key = proj.agency || 'Unknown';
        if (!agencyMap[key]) agencyMap[key] = { name: key, state: agencyStateMap[(key||'').toLowerCase().trim()] || 'Other', weeks: {}, proj1: 0 };
        let wc = 0;
        (proj.visit_details || []).forEach(d => { wc += sharedGetVisitWeight(d.visit_name || d.task_name, d.status, config); });
        agencyMap[key].proj1 += wc;
      });

      const stateOrder = (s) => { const u = (s||'').toUpperCase(); return u === 'TX' ? '0' : u === 'LA' ? '1' : '2_'+u; };
      const data = Object.values(agencyMap).filter(a => ['TX','LA'].includes((a.state||'').toUpperCase())).sort((a,b) => stateOrder(a.state).localeCompare(stateOrder(b.state)) || a.name.localeCompare(b.name));

      const doc = newLandscapePdf();
      drawCompactHeader(doc, { title: 'Agencies Weekly' });

      const headers = ['Agency', 'State', ...weeks.map(w => w.saturdayFormatted), 'Proj 1'];
      const rows = data.map(a => [a.name, a.state, ...weeks.map((_,i) => a.weeks[i] || '-'), a.proj1 || '-']);
      const totals = ['GRAND TOTAL', '', ...weeks.map((_,i) => data.reduce((s,a) => s + (a.weeks[i]||0), 0)), data.reduce((s,a) => s + (a.proj1||0), 0)];
      rows.unshift(totals);

      autoTable(doc, { head: [headers], body: rows, startY: 22, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [26,54,93], textColor: 255 } });
      return doc;
    } catch (e) { console.error('Agencies PDF gen error:', e); return null; }
  };

  const generateEvalDcPDF = (data) => {
    try {
      if (!data || data.length === 0) return null;
      const filtered = data.filter(d => d.agency !== 'Unknown');
      const totals = filtered.reduce((a, r) => ({ evals: a.evals + r.evals, discharges: a.discharges + r.discharges }), { evals: 0, discharges: 0 });

      const doc = newPortraitPdf();
      drawCompactHeader(doc, { title: 'Eval vs Discharge Report', subtitle: `Week Ending: ${data[0]?.week_ending || ''} | Data Source: ${data[0]?.projection_type_used || ''}` });
      doc.setFontSize(12); doc.setTextColor(30,58,69); doc.text(`TOTAL: ${totals.evals} Evals | ${totals.discharges} Discharges | ${totals.evals >= totals.discharges ? '+' : ''}${totals.evals - totals.discharges} Diff`, 14, 32);

      autoTable(doc, {
        head: [['Agency', 'Evals', 'Discharges', 'Difference']],
        body: filtered.map(r => [r.agency, r.evals, r.discharges, `${r.evals >= r.discharges ? '+' : ''}${r.evals - r.discharges}`]),
        startY: 38, styles: { fontSize: 8 }, headStyles: { fillColor: [26,54,93] },
      });
      return doc;
    } catch (e) { console.error('Eval/DC PDF gen error:', e); return null; }
  };

  const generateLateNotesPDF = (data) => {
    try {
      const { week_dates, therapists } = data;
      if (!therapists || therapists.length === 0) return null;

      const doc = newLandscapePdf();
      drawCompactHeader(doc, { title: 'Late Notes Report' });

      const weekHeaders = week_dates.map(d => { const p = d.split('-'); return `${p[1]}/${p[2]}`; });
      autoTable(doc, {
        head: [['Therapist', 'State', ...weekHeaders, 'Total']],
        body: therapists.map(t => [withInactive(t.therapist_name), t.state, ...week_dates.map(d => t.weeks?.[d] || '-'), t.total]),
        startY: 22, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [26,54,93] },
      });
      return doc;
    } catch (e) { console.error('Late Notes PDF gen error:', e); return null; }
  };

  const generateMissedVisitsPDF = (data) => {
    try {
      const { week_dates, therapists } = data;
      if (!therapists || therapists.length === 0) return null;

      const doc = newLandscapePdf();
      drawCompactHeader(doc, { title: 'Missed Visits Report' });

      const weekHeaders = week_dates.map(d => { const p = d.split('-'); return `${p[1]}/${p[2]}`; });
      autoTable(doc, {
        head: [['Therapist', 'State', ...weekHeaders, 'Total']],
        body: therapists.map(t => [withInactive(t.therapist_name), t.state, ...week_dates.map(d => t.weeks?.[d] || '-'), t.total]),
        startY: 22, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [26,54,93] },
      });
      return doc;
    } catch (e) { console.error('Missed PDF gen error:', e); return null; }
  };


  // ---- Date Range Report Generator ---------------------------------------- //
  const handleDateRangeReport = async () => {
    // Calculate date range based on type
    const now = new Date();
    let startDate, endDate;
    
    if (dateRangeType === 'weekly') {
      // Last completed week (Sunday-Saturday)
      const dayOfWeek = now.getDay();
      const daysBack = dayOfWeek === 6 ? 7 : dayOfWeek + 1;
      endDate = new Date(now); endDate.setDate(now.getDate() - daysBack); endDate.setHours(23,59,59);
      startDate = new Date(endDate); startDate.setDate(endDate.getDate() - 6); startDate.setHours(0,0,0);
    } else if (dateRangeType === 'monthly') {
      // Last full month
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (dateRangeType === 'quarterly') {
      // Last full quarter
      const currentQ = Math.floor(now.getMonth() / 3);
      const lastQStart = currentQ === 0 ? new Date(now.getFullYear() - 1, 9, 1) : new Date(now.getFullYear(), (currentQ - 1) * 3, 1);
      const lastQEnd = new Date(lastQStart.getFullYear(), lastQStart.getMonth() + 3, 0, 23, 59, 59);
      startDate = lastQStart; endDate = lastQEnd;
    } else if (dateRangeType === 'annual') {
      startDate = new Date(now.getFullYear() - 1, 0, 1);
      endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
    } else if (dateRangeType === 'custom') {
      if (!customStartDate || !customEndDate) { toast.error('Please select start and end dates'); return; }
      startDate = new Date(customStartDate + 'T00:00:00');
      endDate = new Date(customEndDate + 'T23:59:59');
    }

    const periodLabel = dateRangeType === 'custom'
      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
      : dateRangeType === 'weekly' ? `Week ${(startDate.getMonth()+1)}/${startDate.getDate()} - ${(endDate.getMonth()+1)}/${endDate.getDate()}`
      : dateRangeType === 'monthly' ? `${startDate.toLocaleString('default', {month: 'long', year: 'numeric'})}`
      : dateRangeType === 'quarterly' ? `Q${Math.floor(startDate.getMonth()/3)+1} ${startDate.getFullYear()}`
      : `${startDate.getFullYear()}`;

    // Filter visits by date range
    const filteredVisits = visits.filter(v => {
      const ds = v.date || '';
      let vd;
      if (ds.includes('/')) { const [m,d,y] = ds.split('/'); vd = new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12); }
      else vd = new Date(ds);
      return vd >= startDate && vd <= endDate;
    });

    if (filteredVisits.length === 0) {
      toast.error(`No visits found for ${periodLabel}`);
      return;
    }

    // Generate the selected report type
    const doc = newLandscapePdf();

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    if (dateRangeReport === 'therapists' || dateRangeReport === 'agencies') {
      // Group visits by week for the period
      const getWeekEnding = (d) => {
        const daysToSat = (6 - d.getDay()) % 7;
        const sat = new Date(d); sat.setDate(d.getDate() + daysToSat);
        return sat;
      };
      const weeksSet = new Set();
      filteredVisits.forEach(v => {
        const ds = v.date || '';
        let vd;
        if (ds.includes('/')) { const [m,d,y] = ds.split('/'); vd = new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12); }
        else vd = new Date(ds);
        weeksSet.add(getWeekEnding(vd).toISOString().split('T')[0]);
      });
      const sortedWeeks = [...weeksSet].sort();
      const weekLabels = sortedWeeks.map(w => { const d = new Date(w); return `${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getDate().toString().padStart(2,'0')}`; });

      if (dateRangeReport === 'therapists') {
        drawCompactHeader(doc, { title: `Therapists Report — ${periodLabel}` });
        const therapistMap = {};
        filteredVisits.forEach(v => {
          const key = `${v.first_name} ${v.last_name}`.trim();
          if (!therapistMap[key]) therapistMap[key] = { name: key, state: v.state || '', weeks: {} };
          const ds = v.date || '';
          let vd;
          if (ds.includes('/')) { const [m,d,y] = ds.split('/'); vd = new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12); }
          else vd = new Date(ds);
          const we = getWeekEnding(vd).toISOString().split('T')[0];
          const w = sharedGetVisitWeight(v.task_name, v.status, trackingConfig);
          therapistMap[key].weeks[we] = (therapistMap[key].weeks[we] || 0) + w;
        });
        const data = Object.values(therapistMap).sort((a,b) => a.name.localeCompare(b.name));
        const tableHeaders = ['Therapist', 'State', ...weekLabels, 'Total'];
        const rows = data.map(t => [withInactive(t.name), t.state, ...sortedWeeks.map(w => t.weeks[w] || '-'), Object.values(t.weeks).reduce((s,v) => s+v, 0)]);
        autoTable(doc, { head: [tableHeaders], body: rows, startY: 22, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [26,54,93] } });
      } else {
        drawCompactHeader(doc, { title: `Agencies Report — ${periodLabel}` });
        const agencyMap = {};
        filteredVisits.forEach(v => {
          const key = v.agency || 'Unknown';
          if (!agencyMap[key]) agencyMap[key] = { name: key, weeks: {} };
          const ds = v.date || '';
          let vd;
          if (ds.includes('/')) { const [m,d,y] = ds.split('/'); vd = new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12); }
          else vd = new Date(ds);
          const we = getWeekEnding(vd).toISOString().split('T')[0];
          const w = sharedGetVisitWeight(v.task_name, v.status, trackingConfig);
          agencyMap[key].weeks[we] = (agencyMap[key].weeks[we] || 0) + w;
        });
        const data = Object.values(agencyMap).sort((a,b) => a.name.localeCompare(b.name));
        const tableHeaders = ['Agency', ...weekLabels, 'Total'];
        const rows = data.map(a => [a.name, ...sortedWeeks.map(w => a.weeks[w] || '-'), Object.values(a.weeks).reduce((s,v) => s+v, 0)]);
        autoTable(doc, { head: [tableHeaders], body: rows, startY: 22, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [26,54,93] } });
      }
    } else if (dateRangeReport === 'lateNotes') {
      drawCompactHeader(doc, { title: `Late Notes — ${periodLabel}` });
      const lateVisits = filteredVisits.filter(v => {
        const st = (v.status || '').toLowerCase();
        return st.includes('late') || st.includes('overdue');
      });
      const therapistMap = {};
      lateVisits.forEach(v => {
        const key = `${v.first_name} ${v.last_name}`.trim();
        therapistMap[key] = (therapistMap[key] || 0) + 1;
      });
      const rows = Object.entries(therapistMap).sort((a,b) => b[1]-a[1]).map(([name, count]) => [name, count]);
      autoTable(doc, { head: [['Therapist', 'Late Notes']], body: rows, startY: 22, styles: { fontSize: 9 }, headStyles: { fillColor: [26,54,93] } });
    } else if (dateRangeReport === 'missed') {
      drawCompactHeader(doc, { title: `Missed Visits — ${periodLabel}` });
      const mvVisits = filteredVisits.filter(v => {
        const st = (v.status || '').toLowerCase();
        return st.includes('missed') || st.includes('mv') || st.includes('m/v');
      });
      const therapistMap = {};
      mvVisits.forEach(v => {
        const key = `${v.first_name} ${v.last_name}`.trim();
        therapistMap[key] = (therapistMap[key] || 0) + 1;
      });
      const rows = Object.entries(therapistMap).sort((a,b) => b[1]-a[1]).map(([name, count]) => [name, count]);
      autoTable(doc, { head: [['Therapist', 'Missed Visits']], body: rows, startY: 22, styles: { fontSize: 9 }, headStyles: { fillColor: [26,54,93] } });
    }

    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`${dateRangeReport}_${dateRangeType}_${dateStr}.pdf`);
    toast.success(`${dateRangeReport} report downloaded`);
    setShowDateRangeModal(false);
  };



  // Render report preview modal
  const renderReportPreview = () => {
    if (!reportPreview) return null;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeReportPreview}>
        <Card className="max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b flex items-center justify-between bg-muted/30">
            <h2 className="font-heading text-xl font-semibold">
              {reportPreview === 'weeklySummary' && `Therapists Weekly`}
              {reportPreview === 'weeklySummary' && reportData?.therapists?.some(r => r.pending_review) && (
                <a href="/admin/name-review" className="ml-3 align-middle text-xs font-medium rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 hover:bg-amber-200" data-testid="report-pending-review-badge">
                  {reportData.therapists.filter(r => r.pending_review).length} pending review
                </a>
              )}
              {reportPreview === 'weeklyAgency' && `Agencies Weekly`}
              {reportPreview === 'evalDischarge' && 'Eval vs Discharge Report'}
              {reportPreview === 'lateNotes' && 'Late Notes Report'}
              {reportPreview === 'missedVisits' && `Missed ${trackingConfig?.configured ? trackingConfig.name : 'Visits'} Report`}
              {reportPreview === 'expiringDocs' && `Expiring Documents (${expiringDocsDays === 0 ? 'Expired' : expiringDocsDays + ' Days'})`}
            </h2>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (reportPreview === 'weeklySummary') handleExportWeeklySummaryPDF();
                  else if (reportPreview === 'weeklyAgency') handleExportWeeklyAgencyPDF();
                  else if (reportPreview === 'evalDischarge') handleExportEvalDischargePDF();
                  else if (reportPreview === 'lateNotes') handleExportLateNotesPDF();
                  else if (reportPreview === 'missedVisits') handleExportMissedVisitsPDF();
                  else if (reportPreview === 'expiringDocs') handleExportExpiringDocsPDF(expiringDocsDays);
                }}
                className="gap-2"
                data-testid="download-report-pdf"
              >
                <Download size={16} />
                Download PDF
              </Button>
              <Button variant="ghost" onClick={closeReportPreview} className="h-8 w-8 p-0">
                <X size={18} />
              </Button>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto p-4">
            {reportLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="text-muted-foreground">Loading report...</div>
              </div>
            ) : (
              <>
                {/* Weekly Summary Preview */}
                {reportPreview === 'weeklySummary' && reportData && (
                  <div>
                    <div className="mb-4 text-sm text-muted-foreground">
                      Last 6 Weeks Summary {reportData.hasProj1 && '+ Projections'} | Sorted by State → Last Name
                    </div>
                    <div className="mb-2 text-xs flex gap-4">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500"></span> Within 3 of ideal</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500"></span> 4-5 from ideal</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500"></span> 6+ from ideal</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 sticky left-0 bg-muted">Therapist</th>
                            <th className="text-center p-2 min-w-[50px]">State</th>
                            <th className="text-center p-2 min-w-[60px]">Disc.</th>
                            <th className="text-center p-2 min-w-[50px]">Ideal</th>
                            {reportData.weeks.map((week, idx) => (
                              <th key={idx} className="text-center p-2 min-w-[70px]">
                                {week.saturdayFormatted}
                              </th>
                            ))}
                            {reportData.hasProj1 && (
                              <th className="text-center p-2 min-w-[70px] bg-sage/20">Proj 1</th>
                            )}
                            {reportData.hasProj2 && (
                              <th className="text-center p-2 min-w-[70px] bg-sage/20">Proj 2</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {/* GRAND TOTALS ROW AT TOP */}
                          <tr className="bg-sage text-white font-bold border-b-2 border-sage-dark text-base">
                            <td className="p-3 sticky left-0 bg-sage">GRAND TOTAL</td>
                            <td className="text-center p-3"></td>
                            <td className="text-center p-3"></td>
                            <td className="text-center p-3"></td>
                            {reportData.weekTotals.map((total, idx) => (
                              <td key={idx} className="text-center p-3">{total}</td>
                            ))}
                            {reportData.hasProj1 && (
                              <td className="text-center p-3 bg-sage/80">{reportData.proj1Total}</td>
                            )}
                            {reportData.hasProj2 && (
                              <td className="text-center p-3 bg-sage/80">{reportData.proj2Total}</td>
                            )}
                          </tr>
                          
                          {/* Render therapists grouped by state with subtotals */}
                          {reportData.stateOrder.map((state, stateIdx) => {
                            const therapistsInState = reportData.therapists.filter(t => (t.state || 'Other') === state);
                            const subtotal = reportData.stateSubtotals[state];
                            
                            return (
                              <React.Fragment key={stateIdx}>
                                {therapistsInState.map((t, idx) => {
                                  const getVisitColor = (value) => {
                                    if (t.ideal_visits === null || t.ideal_visits === undefined || value === '-' || value === 0) return '';
                                    const diff = Math.abs(value - t.ideal_visits);
                                    if (diff <= 3) return 'bg-green-100 text-green-800';
                                    if (diff <= 5) return 'bg-yellow-100 text-yellow-800';
                                    return 'bg-red-100 text-red-800';
                                  };
                                  
                                  return (
                                    <tr key={`${stateIdx}-${idx}`} className="border-b">
                                      <td className="p-2 sticky left-0 bg-background">{withInactive(t.name)}</td>
                                      <td className="text-center p-2 text-muted-foreground text-xs">{t.state || '-'}</td>
                                      <td className="text-center p-2 text-muted-foreground">{t.discipline || '-'}</td>
                                      <td className="text-center p-2 font-medium">{t.ideal_visits || '-'}</td>
                                      {reportData.weeks.map((_, wIdx) => {
                                        const value = t.weeks[wIdx] || 0;
                                        const colorClass = getVisitColor(value);
                                        return (
                                          <td key={wIdx} className={`text-center p-2 ${colorClass}`}>
                                            {value || '-'}
                                          </td>
                                        );
                                      })}
                                      {reportData.hasProj1 && (
                                        <td className={`text-center p-2 bg-sage/10 font-medium ${getVisitColor(t.proj1)}`}>{t.proj1 || '-'}</td>
                                      )}
                                      {reportData.hasProj2 && (
                                        <td className={`text-center p-2 bg-sage/10 font-medium ${getVisitColor(t.proj2)}`}>{t.proj2 || '-'}</td>
                                      )}
                                    </tr>
                                  );
                                })}
                                {/* STATE SUBTOTAL ROW */}
                                <tr className="bg-slate-200 font-bold border-b-2 border-slate-400">
                                  <td className="p-2 sticky left-0 bg-slate-200">{state} Total</td>
                                  <td className="text-center p-2"></td>
                                  <td className="text-center p-2"></td>
                                  <td className="text-center p-2"></td>
                                  {subtotal.weekTotals.map((total, idx) => (
                                    <td key={idx} className="text-center p-2">{total}</td>
                                  ))}
                                  {reportData.hasProj1 && (
                                    <td className="text-center p-2 bg-slate-300">{subtotal.proj1Total}</td>
                                  )}
                                  {reportData.hasProj2 && (
                                    <td className="text-center p-2 bg-slate-300">{subtotal.proj2Total}</td>
                                  )}
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Weekly Agency Summary Preview */}
                {reportPreview === 'weeklyAgency' && reportData && (
                  <div>
                    <div className="mb-4 text-sm text-muted-foreground">
                      Last 6 Weeks by Agency {reportData.hasProj1 && '+ Projections'} | Sorted by State → Name
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 sticky left-0 bg-muted">Agency</th>
                            <th className="text-center p-2 min-w-[50px]">State</th>
                            {reportData.weeks.map((week, idx) => (
                              <th key={idx} className="text-center p-2 min-w-[70px]">
                                {week.saturdayFormatted}
                              </th>
                            ))}
                            {reportData.hasProj1 && (
                              <th className="text-center p-2 min-w-[70px] bg-sage/20">Proj 1</th>
                            )}
                            {reportData.hasProj2 && (
                              <th className="text-center p-2 min-w-[70px] bg-sage/20">Proj 2</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {/* GRAND TOTALS ROW AT TOP */}
                          <tr className="bg-sage text-white font-bold border-b-2 border-sage-dark text-base">
                            <td className="p-3 sticky left-0 bg-sage">GRAND TOTAL</td>
                            <td className="text-center p-3"></td>
                            {reportData.weekTotals.map((total, idx) => (
                              <td key={idx} className="text-center p-3">{total}</td>
                            ))}
                            {reportData.hasProj1 && (
                              <td className="text-center p-3 bg-sage/80">{reportData.proj1Total}</td>
                            )}
                            {reportData.hasProj2 && (
                              <td className="text-center p-3 bg-sage/80">{reportData.proj2Total}</td>
                            )}
                          </tr>
                          
                          {/* Render agencies grouped by state with subtotals */}
                          {reportData.stateOrder.map((state, stateIdx) => {
                            const agenciesInState = reportData.agencies.filter(a => (a.state || 'Other') === state);
                            const subtotal = reportData.stateSubtotals[state];
                            
                            return (
                              <React.Fragment key={stateIdx}>
                                {agenciesInState.map((a, idx) => (
                                  <tr key={`${stateIdx}-${idx}`} className="border-b">
                                    <td className="p-2 sticky left-0 bg-background">{a.name}</td>
                                    <td className="text-center p-2 text-muted-foreground text-xs">{a.state || '-'}</td>
                                    {reportData.weeks.map((_, wIdx) => (
                                      <td key={wIdx} className="text-center p-2">
                                        {a.weeks[wIdx] || '-'}
                                      </td>
                                    ))}
                                    {reportData.hasProj1 && (
                                      <td className="text-center p-2 bg-sage/10 font-medium">{a.proj1 || '-'}</td>
                                    )}
                                    {reportData.hasProj2 && (
                                      <td className="text-center p-2 bg-sage/10 font-medium">{a.proj2 || '-'}</td>
                                    )}
                                  </tr>
                                ))}
                                {/* STATE SUBTOTAL ROW */}
                                <tr className="bg-slate-200 font-bold border-b-2 border-slate-400">
                                  <td className="p-2 sticky left-0 bg-slate-200">{state} Total</td>
                                  <td className="text-center p-2"></td>
                                  {subtotal.weekTotals.map((total, idx) => (
                                    <td key={idx} className="text-center p-2">{total}</td>
                                  ))}
                                  {reportData.hasProj1 && (
                                    <td className="text-center p-2 bg-slate-300">{subtotal.proj1Total}</td>
                                  )}
                                  {reportData.hasProj2 && (
                                    <td className="text-center p-2 bg-slate-300">{subtotal.proj2Total}</td>
                                  )}
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Eval vs Discharge Preview */}
                {reportPreview === 'evalDischarge' && reportData && (
                  <div>
                    <div className="mb-4 text-sm text-muted-foreground">
                      Week Ending: {reportData.weekEnding ? new Date(reportData.weekEnding + 'T00:00:00').toLocaleDateString() : 'N/A'} | 
                      Data Source: {reportData.projType || 'Projections'}
                    </div>
                    <div className="bg-muted/50 p-3 rounded-lg mb-4 grid grid-cols-4 gap-4 text-center font-semibold">
                      <div>TOTAL</div>
                      <div className="text-sage">{reportData.totals.evals} Evals</div>
                      <div className="text-clay">{reportData.totals.discharges} Discharges</div>
                      <div className={reportData.totals.difference >= 0 ? 'text-sage' : 'text-red-500'}>
                        {reportData.totals.difference >= 0 ? '+' : ''}{reportData.totals.difference} Diff
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2">Agency</th>
                          <th className="text-center p-2">Evals</th>
                          <th className="text-center p-2">Discharges</th>
                          <th className="text-center p-2">Difference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.items.map((item, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2">{item.agency}</td>
                            <td className="text-center p-2">{item.evals}</td>
                            <td className="text-center p-2">{item.discharges}</td>
                            <td className={`text-center p-2 font-medium ${item.difference >= 0 ? 'text-sage' : 'text-red-500'}`}>
                              {item.difference >= 0 ? '+' : ''}{item.difference}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Late Notes Preview */}
                {reportPreview === 'lateNotes' && reportData && (
                  <div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 sticky left-0 bg-muted">Therapist</th>
                            {reportData.weekDates.map((date, idx) => (
                              <th key={idx} className="text-center p-2 min-w-[80px]">
                                {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Grand Total Row at Top */}
                          <tr className="bg-primary text-white font-bold">
                            <td className="p-2 sticky left-0 bg-primary">GRAND TOTAL</td>
                            {reportData.weekTotals.map((total, idx) => (
                              <td key={idx} className="text-center p-2 text-white">{total}</td>
                            ))}
                          </tr>
                          
                          {/* TX Section */}
                          {reportData.txTherapists && reportData.txTherapists.length > 0 && (
                            <>
                              <tr className="bg-blue-100">
                                <td colSpan={reportData.weekDates.length + 1} className="p-2 font-bold text-primary">
                                  Texas (TX)
                                </td>
                              </tr>
                              {reportData.txTherapists.map((t, idx) => (
                                <tr key={`tx-${idx}`} className="border-b">
                                  <td className="p-2 sticky left-0 bg-background">{withInactive(t.therapist_name)}</td>
                                  {t.weekly_counts.map((count, wIdx) => (
                                    <td key={wIdx} className={`text-center p-2 ${count > 0 ? 'text-orange-600 font-medium' : ''}`}>
                                      {count || '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                              <tr className="bg-blue-200 font-bold">
                                <td className="p-2 sticky left-0 bg-blue-200">TX TOTAL</td>
                                {reportData.txWeekTotals.map((total, idx) => (
                                  <td key={idx} className="text-center p-2">{total}</td>
                                ))}
                              </tr>
                            </>
                          )}
                          
                          {/* LA Section */}
                          {reportData.laTherapists && reportData.laTherapists.length > 0 && (
                            <>
                              <tr className="bg-green-100">
                                <td colSpan={reportData.weekDates.length + 1} className="p-2 font-bold text-primary">
                                  Louisiana (LA)
                                </td>
                              </tr>
                              {reportData.laTherapists.map((t, idx) => (
                                <tr key={`la-${idx}`} className="border-b">
                                  <td className="p-2 sticky left-0 bg-background">{withInactive(t.therapist_name)}</td>
                                  {t.weekly_counts.map((count, wIdx) => (
                                    <td key={wIdx} className={`text-center p-2 ${count > 0 ? 'text-orange-600 font-medium' : ''}`}>
                                      {count || '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                              <tr className="bg-green-200 font-bold">
                                <td className="p-2 sticky left-0 bg-green-200">LA TOTAL</td>
                                {reportData.laWeekTotals.map((total, idx) => (
                                  <td key={idx} className="text-center p-2">{total}</td>
                                ))}
                              </tr>
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Missed Visits Preview */}
                {reportPreview === 'missedVisits' && reportData && (
                  <div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 sticky left-0 bg-muted">Therapist</th>
                            {reportData.weekDates.map((date, idx) => (
                              <th key={idx} className="text-center p-2 min-w-[80px]">
                                {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Grand Total Row at Top */}
                          <tr className="bg-primary text-white font-bold">
                            <td className="p-2 sticky left-0 bg-primary">GRAND TOTAL ({reportData.grandTotal})</td>
                            {reportData.weekTotals.map((total, idx) => (
                              <td key={idx} className="text-center p-2 text-white">{total}</td>
                            ))}
                          </tr>
                          
                          {/* Render each state section */}
                          {reportData.stateOrder.map((state, stateIdx) => {
                            const therapistsInState = reportData.stateGroups[state] || [];
                            const stateWeekTotals = reportData.stateWeekTotals[state] || [];
                            const stateTotal = reportData.stateTotals[state] || 0;
                            const stateColors = {
                              'TX': { header: 'bg-blue-100 text-primary', subtotal: 'bg-blue-200' },
                              'LA': { header: 'bg-green-100 text-primary', subtotal: 'bg-green-200' },
                            };
                            const colors = stateColors[state] || { header: 'bg-orange-100 text-primary', subtotal: 'bg-orange-200' };
                            
                            return (
                              <React.Fragment key={stateIdx}>
                                {/* State Header */}
                                <tr className={colors.header}>
                                  <td colSpan={reportData.weekDates.length + 1} className="p-2 font-bold">
                                    {state === 'Other' ? 'Other' : state}
                                  </td>
                                </tr>
                                {/* Therapists in this state */}
                                {therapistsInState.map((t, idx) => (
                                  <tr key={`${state}-${idx}`} className="border-b">
                                    <td className="p-2 sticky left-0 bg-background">{withInactive(t.therapist_name)}</td>
                                    {t.weekly_counts.map((count, wIdx) => (
                                      <td key={wIdx} className={`text-center p-2 ${count > 0 ? 'text-red-500 font-medium' : ''}`}>
                                        {count || '-'}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                                {/* State Subtotal */}
                                <tr className={`${colors.subtotal} font-bold`}>
                                  <td className={`p-2 sticky left-0 ${colors.subtotal}`}>{state} TOTAL ({stateTotal})</td>
                                  {stateWeekTotals.map((total, idx) => (
                                    <td key={idx} className="text-center p-2">{total}</td>
                                  ))}
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Expiring Docs Preview */}
                {reportPreview === 'expiringDocs' && reportData && (
                  <div>
                    {reportData.missing_count > 0 && (
                      <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-sm text-purple-700">
                          <span className="font-semibold">{reportData.missing_count} missing credentials</span> included below (marked as "None")
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                          Required: {reportData.required_credentials?.join(', ')}
                        </p>
                      </div>
                    )}
                    {reportData.expired.length > 0 && (
                      <div className="mb-6">
                        <h3 className="font-semibold text-red-600 mb-2">Expired / Missing Documents ({reportData.expired.length})</h3>
                        <table className="w-full text-sm">
                          <thead className="bg-red-50">
                            <tr>
                              <th className="text-left p-2">Therapist</th>
                              <th className="text-left p-2">Discipline</th>
                              <th className="text-left p-2">Document</th>
                              <th className="text-left p-2">Expiration Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.expired.map((doc, idx) => (
                              <tr key={idx} className={`border-b ${doc.is_missing ? 'bg-purple-50' : ''}`}>
                                <td className="p-2">{doc.therapist_name}</td>
                                <td className="p-2">{doc.discipline || '-'}</td>
                                <td className="p-2">
                                  {doc.document_name}
                                  {doc.is_missing && <span className="ml-2 text-xs bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded">MISSING</span>}
                                </td>
                                <td className={`p-2 ${doc.is_missing ? 'text-purple-600 font-medium' : 'text-red-600'}`}>
                                  {doc.is_missing ? 'None' : new Date(doc.expiration_date + 'T00:00:00').toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {reportData.expiring.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-amber-600 mb-2">Expiring Soon ({reportData.expiring.length})</h3>
                        <table className="w-full text-sm">
                          <thead className="bg-amber-50">
                            <tr>
                              <th className="text-left p-2">Therapist</th>
                              <th className="text-left p-2">Discipline</th>
                              <th className="text-left p-2">Document</th>
                              <th className="text-left p-2">Expiration Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.expiring.map((doc, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="p-2">{doc.therapist_name}</td>
                                <td className="p-2">{doc.discipline || '-'}</td>
                                <td className="p-2">{doc.document_name}</td>
                                <td className="p-2 text-amber-600">{new Date(doc.expiration_date + 'T00:00:00').toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {reportData.expired.length === 0 && reportData.expiring.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">No documents found</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Report Preview Modal */}
      {renderReportPreview()}

      {/* Email Reports Modal (extracted) */}
      <EmailReportsModal
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        emailRecipients={emailRecipients}
        setEmailRecipients={setEmailRecipients}
        emailReports={emailReports}
        setEmailReports={setEmailReports}
        emailMessage={emailMessage}
        setEmailMessage={setEmailMessage}
        emailSending={emailSending}
        onSend={handleSendEmailReports}
      />

      {/* Date Range Report Modal (extracted) */}
      <DateRangeReportModal
        open={showDateRangeModal}
        onClose={() => setShowDateRangeModal(false)}
        dateRangeReport={dateRangeReport}
        setDateRangeReport={setDateRangeReport}
        dateRangeType={dateRangeType}
        setDateRangeType={setDateRangeType}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        onGenerate={handleDateRangeReport}
      />

      
      {!hideHeader && (
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            {trackingConfig?.configured ? trackingConfig.name : 'Visits'}
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Search, filter, and export {unitLabel()} records
          </p>
        </div>
      )}

      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-lg font-semibold">Filters</h3>
          <div className="flex flex-wrap gap-1">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="gap-1 text-xs px-2"
                data-testid="clear-filters-button"
              >
                <X size={14} />
                Clear
              </Button>
            )}
            <Button
              onClick={loadWeeklySummaryPreview}
              disabled={exporting || reportLoading || visits.length === 0}
              variant="outline"
              size="sm"
              className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
              data-testid="export-weekly-pdf-button"
            >
              <FileText size={14} />
              Therapists
            </Button>
            <Button
              onClick={loadWeeklyAgencySummaryPreview}
              disabled={exporting || reportLoading || visits.length === 0}
              variant="outline"
              size="sm"
              className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
              data-testid="export-weekly-agency-button"
            >
              <FileText size={14} />
              Agencies
            </Button>
            <Button
              onClick={loadEvalDischargePreview}
              disabled={exporting || reportLoading}
              variant="outline"
              size="sm"
              className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
              data-testid="export-eval-discharge-button"
            >
              <FileText size={14} />
              Eval/DC
            </Button>
            <Button
              onClick={loadLateNotesPreview}
              disabled={exporting || reportLoading}
              variant="outline"
              size="sm"
              className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
              data-testid="export-late-notes-button"
            >
              <FileText size={14} />
              Late Notes
            </Button>
            <Button
              onClick={loadMissedVisitsPreview}
              disabled={exporting || reportLoading}
              variant="outline"
              size="sm"
              className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
              data-testid="export-missed-visits-button"
            >
              <FileText size={14} />
              Missed
            </Button>
            <div className="relative inline-block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={exporting || reportLoading}
                    className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
                    data-testid="export-expiring-docs-button"
                  >
                    <FileText size={14} />
                    Expiring
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => loadExpiringDocsPreview(0)}>Expired</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => loadExpiringDocsPreview(30)}>30 Days</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => loadExpiringDocsPreview(60)}>60 Days</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => loadExpiringDocsPreview(90)}>90 Days</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button
              onClick={handleExportExcel}
              disabled={exporting || visits.length === 0}
              size="sm"
              className="gap-1 text-xs px-2"
              data-testid="export-button"
            >
              <FileSpreadsheet size={14} />
              Excel
            </Button>
            {(isAdmin || hasPermission('email_reports')) && (
            <Button
              onClick={() => setShowEmailModal(true)}
              disabled={exporting || visits.length === 0}
              size="sm"
              variant="outline"
              className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
              data-testid="email-reports-button"
            >
              <Mail size={14} />
              Email
            </Button>
            )}
            <Button
              onClick={() => setShowDateRangeModal(true)}
              disabled={exporting || visits.length === 0}
              size="sm"
              variant="outline"
              className="gap-1 text-xs px-2 hover:bg-primary hover:text-white hover:border-primary"
              data-testid="date-range-report-button"
            >
              <Download size={14} />
              By Period
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Quick Date Range</Label>
            <Select
              value={filters.date_range_preset}
              onValueChange={handleDateRangePreset}
            >
              <SelectTrigger data-testid="date-range-preset">
                <SelectValue placeholder="Select preset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_week">Last Week</SelectItem>
                <SelectItem value="last_2_weeks">Last 2 Weeks</SelectItem>
                <SelectItem value="last_4_weeks">Last 4 Weeks</SelectItem>
                <SelectItem value="last_6_weeks">Last 6 Weeks</SelectItem>
                <SelectItem value="last_8_weeks">Last 8 Weeks</SelectItem>
                <SelectItem value="last_12_weeks">Last 12 Weeks</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Profiles</Label>
            <MultiSelect
              options={profiles.map(p => ({ value: p.id, label: `${p.last_name}, ${p.first_name}` }))}
              selected={filters.profile_ids}
              onChange={(values) => setFilters({ ...filters, profile_ids: values })}
              placeholder="All profiles"
              testId="profile-filter"
            />
          </div>

          <div className="space-y-2">
            <Label>Agency</Label>
            <MultiSelect
              options={agencies.map(a => ({ value: a.agency_name, label: a.agency_name }))}
              selected={filters.agency_names}
              onChange={(values) => setFilters({ ...filters, agency_names: values })}
              placeholder="All agencies"
              testId="agency-filter"
            />
          </div>

          <div className="space-y-2">
            <Label>Task Type</Label>
            <MultiSelect
              options={taskNames.map(t => ({ value: t, label: t }))}
              selected={filters.task_names}
              onChange={(values) => setFilters({ ...filters, task_names: values })}
              placeholder="All task types"
              testId="task-filter"
            />
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <MultiSelect
              options={statuses.map(s => ({ value: s, label: s }))}
              selected={filters.status_types}
              onChange={(values) => setFilters({ ...filters, status_types: values })}
              placeholder="All statuses"
              testId="status-filter"
            />
          </div>

          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              data-testid="start-date-input"
            />
          </div>

          <div className="space-y-2">
            <Label>End Date</Label>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              data-testid="end-date-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading visits...</div>
        ) : visits.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto mb-4 text-muted-foreground" size={48} strokeWidth={1.5} />
            <p className="text-muted-foreground">No {unitLabel()} found</p>
          </div>
        ) : (
          <div>
            <div className="mb-4 text-sm text-muted-foreground">
              Showing {visits.length} {visits.length !== 1 ? unitLabel(true) : unitLabel(false)}
            </div>
            <div className="overflow-auto rounded-lg border border-border">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                      Name
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                      Date
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                      Task Name
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                      Task Type
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                      Status
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-4">
                      Agency
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map((visit) => (
                    <tr
                      key={visit.id}
                      className="border-b border-border hover:bg-muted/20 transition-colors"
                      data-testid="visit-row"
                    >
                      <td className="p-4 text-sm text-foreground font-medium">
                        {withInactive(`${visit.first_name} ${visit.last_name}`)}
                      </td>
                      <td className="p-4 text-sm text-foreground">
                        {visit.date || '-'}
                      </td>
                      <td className="p-4 text-sm text-foreground">
                        {visit.task_name || '-'}
                      </td>
                      <td className="p-4 text-sm text-foreground">
                        {visit.task_type || '-'}
                      </td>
                      <td className="p-4 text-sm text-foreground">
                        {visit.status || '-'}
                      </td>
                      <td className="p-4 text-sm text-foreground">
                        {visit.agency || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
      
      {/* Missing State Modal */}
      {showMissingStateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-destructive">
                Missing State Assignment
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setShowMissingStateModal(false);
                  setMissingStateItems([]);
                  setPendingReportType(null);
                }}
              >
                <X size={16} />
              </Button>
            </div>
            
            <p className="text-muted-foreground mb-4">
              The following {pendingReportType === 'therapist' ? 'therapists' : 'agencies'} don't have a state assigned. 
              Please assign a state (TX or LA) to include them in the report totals.
            </p>
            
            <div className="space-y-3">
              {missingStateItems.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Proj 1: {item.proj1} visits, Proj 2: {item.proj2} visits
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={item.newState || ''}
                      onValueChange={(val) => {
                        setMissingStateItems(items => 
                          items.map((it, i) => i === idx ? { ...it, newState: val } : it)
                        );
                      }}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TX">TX</SelectItem>
                        <SelectItem value="LA">LA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMissingStateModal(false);
                  setMissingStateItems([]);
                  setPendingReportType(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  // Check all items have states assigned
                  const unassigned = missingStateItems.filter(i => !i.newState);
                  if (unassigned.length > 0) {
                    toast.error('Please assign a state to all items');
                    return;
                  }
                  
                  // Update the states in the database
                  try {
                    const token = localStorage.getItem('token');
                    for (const item of missingStateItems) {
                      if (item.type === 'therapist') {
                        // Find the personnel record and update state (case-insensitive)
                        const personnelRes = await axios.get(`${API}/personnel`);
                        const normalizedItemName = (item.name || '').toLowerCase().trim();
                        const personnel = personnelRes.data.find(p => 
                          `${p.first_name} ${p.last_name}`.toLowerCase().trim() === normalizedItemName
                        );
                        if (personnel) {
                          await axios.put(
                            `${API}/personnel/${personnel.id}`,
                            { state: item.newState },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                        } else {
                          console.error(`Personnel not found: ${item.name}`);
                        }
                      } else {
                        // Update agency state (case-insensitive match)
                        const agenciesRes = await axios.get(`${API}/agency-profiles`);
                        const normalizedItemName = (item.name || '').toLowerCase().trim();
                        const agency = agenciesRes.data.find(a => 
                          (a.agency_name || '').toLowerCase().trim() === normalizedItemName
                        );
                        if (agency) {
                          await axios.put(
                            `${API}/agency-profiles/${agency.id}`,
                            { state: item.newState },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                        } else {
                          // If agency doesn't exist, create it
                          console.log(`Creating new agency: ${item.name}`);
                          await axios.post(
                            `${API}/agency-profiles`,
                            { agency_name: item.name, state: item.newState },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                        }
                      }
                    }
                    
                    toast.success('States updated successfully');
                    setShowMissingStateModal(false);
                    setMissingStateItems([]);
                    
                    // Re-run the report
                    if (pendingReportType === 'therapist') {
                      setPendingReportType(null);
                      // Refresh data and regenerate report
                      await fetchInitialData();
                      loadWeeklySummaryPreview();
                    } else {
                      setPendingReportType(null);
                      await fetchInitialData();
                      loadWeeklyAgencySummaryPreview();
                    }
                  } catch (error) {
                    console.error('Error updating states:', error);
                    toast.error('Failed to update states');
                  }
                }}
              >
                Save & Generate Report
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}